import { useState, useCallback, useEffect, useRef } from "react";
import { C, uid, fmt, pesoActual, Badge, Btn, Input, Select, Textarea, Modal, Section, CollapsibleSection, StatCard, Tabs, RazaInput, useIsMobile, SEMILLAS_OPC, ESPECIES, RAZAS, catsDe, FEATURE_IDS, isFeatureOn, Switch } from "./components/ui";
import { estadoService, potreroService, animalService, perfilService, preciosService, semillasService, iaService, supabase, actividadService, pesadaService, eventoReproduccionService, movimientoService, tratamientoService, distribucionFardoService, movimientoInsumoService } from "./services/supabase";
import { usePotrero } from "./hooks/usePotrero";
import { useAnimales } from "./hooks/useAnimales";
import { useSanidad } from "./hooks/useSanidad";
import { useEconomia } from "./hooks/useEconomia";
import { useFardos } from "./hooks/useFardos";
import { useTareas } from "./hooks/useTareas";
import { useEmpleados } from "./hooks/useEmpleados";
import { useMaquinarias } from "./hooks/useMaquinarias";
import { useZonas } from "./hooks/useZonas";
import { useInsumos } from "./hooks/useInsumos";
import { toast } from "./services/toast";
import { HomePanel } from "./components/Home";
import { Buscador } from "./components/Buscador";
import { ReproduccionPanel } from "./components/Reproduccion";
import { MapaLeaflet } from "./components/Mapa";
import { RodeoPanel, FichaAnimal } from "./components/Rodeo";
import { SanidadPanel } from "./components/Sanidad";
import { EconomiaPanel } from "./components/Economia";
import { FardosPanel } from "./components/Fardos";
import { InsumosPanel } from "./components/Insumos";
import { CalendarioPanel } from "./components/Agenda";
import { GestionPanel } from "./components/Gestion";
import { MonitorPanel } from "./components/Monitor";
import { ZonasPanel, TIPOS_ZONA } from "./components/Zonas";
import { PublicacionesPanel } from "./components/Publicaciones";
import { MarketplacePanel } from "./components/Marketplace";
import { AdminPanel } from "./components/Admin";

// ─── CONSTANTES ───────────────────────────────────────────
const ESTADO_COLOR = { activo:C.activo, descanso:C.descanso, disponible:C.disponible, sembrado:C.sembrado };
const ESTADO_LABEL = { activo:"Pastoreando", descanso:"Descanso", disponible:"Disponible", sembrado:"Sembrado" };

const NAV = [
  { id:"inicio",      ic:"🏠", l:"Inicio" },
  { id:"mapa",        ic:"🗺️", l:"Mapa" },
  { id:"rodeo",       ic:"🐄", l:"Rodeo" },
  { id:"sanidad",     ic:"💉", l:"Sanidad" },
  { id:"reproduccion", ic:"🐮", l:"Reproducción" },
  { id:"agenda",      ic:"🗓️", l:"Agenda" },
  { id:"monitor",     ic:"📊", l:"Monitor" },
  { id:"gestion",     ic:"🚜", l:"Gestión" },
  { id:"economia",    ic:"💰", l:"Economía" },
  { id:"fardos",      ic:"🌾", l:"Fardos" },
  { id:"insumos",     ic:"📦", l:"Insumos" },
  { id:"zonas",       ic:"🐔", l:"Zonas" },
  { id:"publicaciones", ic:"🛒", l:"Mercado" },
  { id:"marketplace", ic:"🏪", l:"Tienda" },
  { id:"admin",       ic:"🔐", l:"Admin" },
];
const NAV_MAP = Object.fromEntries(NAV.map(n => [n.id, n]));
const NAV_GROUPS = [
  { label:null,          ids:["inicio"] },
  { label:"Campo",       ids:["mapa","zonas"] },
  { label:"Hacienda",    ids:["rodeo","sanidad","reproduccion"] },
  { label:"Stock",       ids:["insumos","fardos"] },
  { label:"Gestión",     ids:["agenda","gestion","monitor"] },
  { label:"Plata y red", ids:["economia","publicaciones","marketplace"] },
  { label:null,          ids:["admin"] },
];

const CLIMA_MOCK = [
  { ic:"⛅", dia:"Lun", max:18, min:9,  lluvia:0,  prob:10 },
  { ic:"🌤", dia:"Mar", max:22, min:11, lluvia:0,  prob:5 },
  { ic:"🌧", dia:"Mié", max:14, min:8,  lluvia:12, prob:80 },
  { ic:"🌧", dia:"Jue", max:13, min:7,  lluvia:8,  prob:70 },
  { ic:"⛅", dia:"Vie", max:17, min:9,  lluvia:1,  prob:20 },
  { ic:"☀️", dia:"Sáb", max:21, min:10, lluvia:0,  prob:0 },
  { ic:"☀️", dia:"Dom", max:23, min:12, lluvia:0,  prob:0 },
];
const DIAS = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];

const ALERTAS_AGRO = [
  { ic:"❄️", nivel:"danger", title:"Riesgo de heladas",           periodo:"26–29 jun", fuente:"INUMET", desc:"Mínimas previstas de -2°C a 1°C en zonas bajas durante la madrugada. Protegé praderas sembradas este otoño y reforzá el agua de los bebederos (riesgo de congelamiento)." },
  { ic:"🌿", nivel:"warn",   title:"Crecimiento de pasto lento",  periodo:"todo junio", fuente:"Plan Agropecuario", desc:"Con temperaturas medias bajo 8°C el raigrás y la festuca casi no crecen (tasa < 10 kg MS/ha/día). Ajustá la carga animal y planificá suplementación con fardos." },
  { ic:"⚡", nivel:"warn",   title:"Fenómeno La Niña (neutro)",   periodo:"jun–ago 2026", fuente:"IRI/NOAA", desc:"Probabilidad de precipitaciones levemente por debajo del promedio para el trimestre. Vigilá reservas de agua para el invierno." },
  { ic:"💧", nivel:"ok",     title:"Humedad de suelo: adecuada",  periodo:"actualizado 23 jun", fuente:"INIA-GRAS", desc:"Reservas de agua en el rango óptimo para el oeste del país. Sin déficit ni excesos hídricos generalizados." },
  { ic:"🐛", nivel:"warn",   title:"Vigilancia sanitaria",        periodo:"otoño-invierno", fuente:"MGAP", desc:"Época de mayor carga parasitaria (lombrices) en lanares jóvenes. Considerá un análisis de materia fecal (HPG) antes de desparasitar." },
  { ic:"🌬", nivel:"ok",     title:"Vientos: normales",           periodo:"próximos 7 días", fuente:"INUMET", desc:"Sin alertas de vientos fuertes para el litoral oeste en la semana." },
];

// Precios de referencia del agro (USD/kg). Valores reales ACG — actualizar semanalmente.
// Fuente: https://acg.com.uy  (Asociación de Consignatarios de Ganado)
const PRECIOS_REF = {
  fecha: "Semana 25 · 16–22 jun 2026",
  fuente: "ACG",
  items: [
    // Ganado gordo (4ª balanza)
    { ic:"🐂", label:"Novillo",            val:"5,57", uni:"USD/kg 4ª" },
    { ic:"🐄", label:"Vaca",               val:"5,19", uni:"USD/kg 4ª" },
    { ic:"🐮", label:"Vaquillona",         val:"5,42", uni:"USD/kg 4ª" },
    // Ovinos (4ª balanza)
    { ic:"🐑", label:"Cordero",            val:"6,13", uni:"USD/kg 4ª" },
    { ic:"🐏", label:"Capón",              val:"5,20", uni:"USD/kg 4ª" },
    { ic:"🐏", label:"Oveja",              val:"5,11", uni:"USD/kg 4ª" },
    // Reposición (en pie)
    { ic:"🐃", label:"Ternero",            val:"4,19", uni:"USD/kg pie" },
    { ic:"🐃", label:"Ternera",            val:"3,99", uni:"USD/kg pie" },
    { ic:"🌾", label:"Vaca invernada",     val:"2,33", uni:"USD/kg pie" },
  ],
};

// Precios de referencia de semillas (USD). Fuente: https://www.anaprose.com.uy
const SEMILLAS_REF = {
  fecha: "Zafra 2025/26",
  fuente: "ANAPROSE",
  items: [
    { ic:"🌱", label:"Soja RR1",        val:"0.83", uni:"USD/kg" },
    { ic:"🌱", label:"Soja Intacta",    val:"1.33", uni:"USD/kg" },
    { ic:"🌾", label:"Moha",            val:"1.08", uni:"USD/kg" },
    { ic:"🌾", label:"Sorgo forraj.",   val:"1.15", uni:"USD/kg" },
    { ic:"🌽", label:"Maíz RR-Bt",      val:"130",  uni:"USD/bolsa" },
    { ic:"🌾", label:"Avena byz.",      val:"0.48", uni:"USD/kg" },
    { ic:"🌿", label:"Raigrás E284",    val:"1.11", uni:"USD/kg" },
    { ic:"🌿", label:"Festuca Tacuabé", val:"3.20", uni:"USD/kg" },
  ],
};

function wxIcon(code) {
  if (code===0) return "☀️";
  if (code<=2)  return "🌤";
  if (code<=3)  return "☁️";
  if (code<=48) return "🌫";
  if (code<=67) return "🌧";
  if (code<=77) return "🌨";
  if (code<=82) return "🌦";
  return "⛈";
}

// Normalizar nombres de columnas de animal: camelCase → snake_case + limpiar valores vacíos
function normalizeAnimal(animal) {
  if (!animal) return animal;
  const { pesadas, eventos, movimientos, sanidad, ...rest } = animal;
  const normalized = {};
  for (const [key, value] of Object.entries(rest)) {
    // Mapeo de camelCase a snake_case
    const snakeKey = key
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .toLowerCase();
    // No incluir valores vacíos o null
    if (value !== "" && value !== null && value !== undefined) {
      normalized[snakeKey] = value;
    }
  }
  return normalized;
}

// Convertir snake_case a camelCase (inverso de normalizeAnimal)
function denormalizeData(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const denormalized = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    denormalized[camelKey] = value;
  }
  return denormalized;
}

// Fila de `animales` (BD) → animal como lo espera la UI: camelCase + las
// sub-colecciones anidadas. `detalles` viene de animalService.getDetallesPorCampo.
// Sin esto pesoActual() lee undefined y todo el rodeo muestra 0 tras recargar.
function hidratarAnimal(row, detalles) {
  const a = denormalizeData(row);
  const id = row?.id;
  return {
    ...a,
    pesadas:     detalles?.pesadas?.[id]     || [],
    eventos:     detalles?.eventos?.[id]     || [],
    sanidad:     detalles?.sanidad?.[id]     || [],
    movimientos: detalles?.movimientos?.[id] || [],
  };
}

// Área en hectáreas desde coordenadas geográficas [lat, lng]
// Parsea números aceptando coma o punto decimal ("0,5" → 0.5)
function numCom(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(String(v).replace(",", "."));
  return isNaN(n) ? null : n;
}

function calcAreaHaGeo(pts) {
  if (!pts || pts.length < 3) return 0;
  const R    = 111320;
  const lat0 = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const lngR = R * Math.cos(lat0 * Math.PI / 180);
  let area = 0;
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += (pts[i][1] * lngR) * (pts[j][0] * R) - (pts[j][1] * lngR) * (pts[i][0] * R);
  }
  return Math.abs(area / 2) / 10000;
}

// Item de stat en línea para la barra de resumen (full width)
function StatInline({ val, label, ic, last }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ display:"flex", alignItems:"center", gap: isMobile?6:9, flexShrink:0,
      padding: isMobile?"0 12px":"0 22px", borderRight:last?"none":`1px solid ${C.arenaDark}`, height: isMobile?30:34 }}>
      <span style={{ fontSize: isMobile?14:17 }}>{ic}</span>
      <div style={{ display:"flex", flexDirection:"column", lineHeight:1 }}>
        <span style={{ fontFamily:"serif", fontSize: isMobile?16:21, fontWeight:700, color:C.tierra }}>{val}</span>
        <span style={{ fontSize: isMobile?9:10.5, color:C.muted, marginTop:3, textTransform:"uppercase", letterSpacing:"0.04em", whiteSpace:"nowrap" }}>{label}</span>
      </div>
    </div>
  );
}

// Tarjeta individual de precio
function PrecioCard({ p }) {
  // ▲ subió (verde) · ▼ bajó (rojo) · = se mantuvo (azul) · sin dato → nada
  const tc = p.tend==="up"?"#1A8A3C":p.tend==="down"?C.danger:p.tend==="flat"?"#2A6FB0":null;
  const ta = p.tend==="up"?"▲":p.tend==="down"?"▼":p.tend==="flat"?"=":null;
  return (
    <div style={{ flexShrink:0, background:C.arena, borderRadius:8, padding:"7px 9px", minWidth:78, textAlign:"center" }}>
      <div style={{ fontSize:12, color:C.muted, marginBottom:2, whiteSpace:"nowrap", fontWeight:500 }}>
        {p.ic} {p.label}
      </div>
      <div style={{ display:"flex", alignItems:"baseline", gap:3, justifyContent:"center" }}>
        <span style={{ fontFamily:"serif", fontSize:24, fontWeight:700, color:C.tierra, lineHeight:1 }}>{p.val}</span>
        {ta && <span style={{ fontSize:10, color:tc, fontWeight:700 }}>{ta}</span>}
      </div>
      <div style={{ fontSize:10, color:C.muted, fontFamily:"monospace", whiteSpace:"nowrap", marginTop:2 }}>{p.uni}</div>
    </div>
  );
}

// Tira de precios: carrusel que gira solo + se puede arrastrar a mano
function PreciosStrip({ data }) {
  const ref = useRef(null);
  const pos = useRef(0);                    // posición en float (evita redondeo a entero)
  const pausa = useRef(false);              // pausa el auto-scroll (hover)
  const drag = useRef({ active:false, x:0 });

  useEffect(() => {
    const el = ref.current; if (!el) return;
    let raf;
    const step = () => {
      if (el && !pausa.current && !drag.current.active) {
        const half = el.scrollWidth / 2;
        if (half > 0) {
          pos.current += 0.5;
          if (pos.current >= half) pos.current -= half; // loop sin salto
          el.scrollLeft = pos.current;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onDown = (e) => { drag.current = { active:true, x:e.clientX }; };
  const onMove = (e) => {
    if (!drag.current.active) return;
    const el = ref.current;
    let next = el.scrollLeft - (e.clientX - drag.current.x);
    const half = el.scrollWidth / 2;
    if (half > 0) { if (next < 0) next += half; else if (next >= half) next -= half; }
    el.scrollLeft = next;
    pos.current = next;                     // sincroniza para que el auto-scroll siga desde acá
    drag.current.x = e.clientX;
  };
  const onUp = () => { drag.current.active = false; };

  return (
    <>
      <style>{`.campoia-carrusel::-webkit-scrollbar{display:none;}`}</style>
      <div
        ref={ref}
        className="campoia-carrusel"
        onMouseEnter={()=>{ pausa.current = true; }}
        onMouseLeave={()=>{ pausa.current = false; drag.current.active = false; }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        style={{ display:"flex", gap:6, overflowX:"auto", cursor:"grab", touchAction:"pan-x",
          scrollbarWidth:"none", msOverflowStyle:"none", userSelect:"none" }}
      >
        {[...data.items, ...data.items].map((p,i)=> <PrecioCard key={i} p={p}/>)}
      </div>
      <div style={{ fontSize:10.5, color:C.muted, fontFamily:"monospace", marginTop:7 }}>
        {data.fecha} · {data.fuente}{data.live ? " 🟢" : ""}
      </div>
    </>
  );
}

// Tira de pronóstico semanal con lluvia (reutilizable)
function ForecastStrip({ clima }) {
  return (
    <div style={{ display:"flex", gap:5, overflowX:"auto" }}>
      {clima.map((d,i)=>(
        <div key={i} style={{ flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center",
          gap:2, background:C.arena, borderRadius:8, padding:"8px 8px", fontSize:10, minWidth:44 }}>
          <div style={{ color:C.muted, fontFamily:"monospace" }}>{d.dia}</div>
          <div style={{ fontSize:18 }}>{d.ic}</div>
          <div style={{ fontFamily:"monospace", fontSize:12, color:C.tierra, fontWeight:700 }}>{d.max}°</div>
          <div style={{ fontFamily:"monospace", fontSize:9, color:C.muted }}>{d.min}°</div>
          <div style={{ fontFamily:"monospace", fontSize:9.5, color: d.prob>=50?"#2A6FB0":C.muted, fontWeight: d.prob>=50?700:400, whiteSpace:"nowrap" }}>
            💧{d.prob}%
          </div>
          {d.lluvia>0 && <div style={{ fontSize:8.5, color:"#2A6FB0", fontFamily:"monospace" }}>{d.lluvia}mm</div>}
        </div>
      ))}
    </div>
  );
}

// ─── MODAL NUEVO POTRERO ──────────────────────────────────
function ModalNuevoPotrero({ count, onSave, onCancel }) {
  const [nombre, setNombre] = useState(`Potrero ${count}`);
  const [tipo,   setTipo]   = useState("cultivado"); // "cultivado" o "libre"
  const [pasto,  setPasto]  = useState("Raigrás");
  const [estado, setEstado] = useState("disponible");
  const [ha,     setHa]     = useState("");
  const [obs,    setObs]    = useState("");

  return (
    <Modal title="Nuevo potrero" onClose={onCancel}>
      <Input label="Nombre" value={nombre} onChange={setNombre} required placeholder="Ej: La Cuchilla, El Bajo..."/>

      <Select label="Tipo de potrero" value={tipo} onChange={setTipo}
        options={[
          {v:"cultivado", l:"🌾 Cultivado (con siembra/fertilización)"},
          {v:"libre", l:"🌾 A campo libre (solo pastoreo)"}
        ]}/>

      {tipo === "cultivado" ? (
        <Select label="Tipo de pasto" value={pasto} onChange={setPasto}
          options={["Raigrás","Festuca","Campo natural","Trébol + raigrás","Lotus","Mezcla"]}/>
      ) : (
        <div style={{ background:C.arena, borderRadius:8, padding:"10px 12px", marginBottom:14, fontSize:12, color:C.muted }}>
          🌿 Campo libre — Sin cultivos específicos. Solo disponible para pastoreo y descanso.
        </div>
      )}

      <Select label="Estado inicial" value={estado} onChange={setEstado}
        options={[
          {v:"disponible",l:"Disponible"},
          {v:"activo",l:"En pastoreo"},
          {v:"descanso",l:"En descanso"},
          ...(tipo==="cultivado" ? [{v:"sembrado",l:"Recién sembrado"}] : [])
        ]}/>

      <Input label="Superficie (ha)" value={ha} onChange={setHa} placeholder="Ej: 0,5 (vacío = la calcula del dibujo)"/>
      <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2} placeholder="Ubicación, características, notas..."/>

      <div style={{ display:"flex", gap:8, marginTop:4 }}>
        <Btn variant="ghost" onClick={onCancel} style={{flex:1}}>Cancelar</Btn>
        <Btn variant="primary" onClick={()=>onSave({nombre,pasto,estado,haReal:numCom(ha),obs,tipo})} style={{flex:1}}>Guardar</Btn>
      </div>
    </Modal>
  );
}

// ─── FICHA POTRERO ────────────────────────────────────────
function TabInfo({ pot, onUpdate, onDelete, onClose }) {
  const [estado,        setEstado]        = useState(pot.estado);
  const [tipo,          setTipo]          = useState(pot.tipo||"cultivado");
  const [pasto,         setPasto]         = useState(pot.pasto);
  const [obs,           setObs]           = useState(pot.obs||"");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [guardado,      setGuardado]      = useState(false);
  const guardar = () => {
    onUpdate({ ...pot, estado, tipo, pasto, obs, color:ESTADO_COLOR[estado] });
    setGuardado(true);
    setTimeout(() => { onClose ? onClose() : setGuardado(false); }, 900);
  };
  const nAnimales = pot.animalesAsignados?.length || 0;

  const estadoOpts = tipo==="libre"
    ? [{v:"disponible",l:"Disponible"},{v:"activo",l:"En pastoreo"},{v:"descanso",l:"En descanso"}]
    : [{v:"disponible",l:"Disponible"},{v:"activo",l:"En pastoreo"},{v:"descanso",l:"En descanso"},{v:"sembrado",l:"Sembrado"}];

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:20 }}>
        <StatCard val={pot.ha.toFixed(1)} label="hectáreas"/>
        <StatCard val={nAnimales} label="animales"/>
        <StatCard val={pot.actividades?.length||0} label="actividades"/>
      </div>

      <Select label="Tipo de potrero" value={tipo} onChange={v=>{setTipo(v);if(v==="libre") setPasto("Campo natural");}}
        options={[
          {v:"cultivado", l:"🌾 Cultivado (con siembra/fertilización)"},
          {v:"libre", l:"🌾 A campo libre (solo pastoreo)"}
        ]}/>

      {tipo==="cultivado" && (
        <Select label="Tipo de pasto" value={pasto} onChange={setPasto}
          options={["Raigrás","Festuca","Campo natural","Trébol + raigrás","Lotus","Mezcla"]}/>
      )}

      <Select label="Estado" value={estado} onChange={setEstado} options={estadoOpts}/>

      <Textarea label="Observaciones" value={obs} onChange={setObs} rows={3}/>
      <Btn variant="primary" onClick={guardar} disabled={guardado} style={{width:"100%"}}>
        {guardado ? "✓ Guardado" : "Guardar cambios"}
      </Btn>

      {/* Zona de eliminación */}
      <div style={{ marginTop:20, paddingTop:16, borderTop:`1px solid ${C.arenaDark}` }}>
        {!confirmDelete ? (
          <Btn variant="danger" size="sm" onClick={()=>setConfirmDelete(true)}>
            Eliminar potrero
          </Btn>
        ) : (
          <div style={{ background:"#FAE8E8", borderRadius:9, padding:"12px 14px",
            border:"1px solid #E09090" }}>
            <div style={{ fontSize:13, fontWeight:600, color:C.danger, marginBottom:6 }}>
              ¿Eliminar "{pot.nombre}"?
            </div>
            {nAnimales > 0 && (
              <div style={{ fontSize:12, color:C.danger, marginBottom:10, lineHeight:1.5 }}>
                ⚠ Este potrero tiene <b>{nAnimales} animal{nAnimales!==1?"es":""}</b> — quedarán sin asignación al eliminarlo.
              </div>
            )}
            <div style={{ display:"flex", gap:8 }}>
              <Btn size="sm" variant="ghost" onClick={()=>setConfirmDelete(false)} style={{flex:1}}>
                Cancelar
              </Btn>
              <Btn size="sm" variant="danger" onClick={onDelete} style={{flex:1}}>
                Sí, eliminar
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Campos que aplican por tipo de actividad
const CAMPOS_ACT = {
  "Siembra":           ["semilla","dosis_agro","horas","litros","costo"],
  "Fertilización":     ["producto","dosis_agro","horas","litros","costo"],
  "Herbicida":         ["producto","dosis_agro","horas","litros","costo"],
  "Insecticida":       ["producto","dosis_agro","horas","litros","costo"],
  "Fungicida":         ["producto","dosis_agro","horas","litros","costo"],
  "Riego":             ["horas","costo"],
  "Pastoreo":          ["costo"],
  "Descanso":          [],
  "Análisis de suelo": ["costo"],
  "Laboreo":           ["horas","litros","costo"],
  "Enfardar":          ["pasto_fardo","cant_fardos","tipo_fardo","litros","costo"],
  "Otro":              ["producto","dosis_agro","horas","litros","costo"],
};

// Actividades permitidas por tipo de potrero
const TIPOS_ACT_CULTIVADO = Object.keys(CAMPOS_ACT); // Todas
const TIPOS_ACT_LIBRE = ["Pastoreo", "Descanso", "Riego", "Enfardar", "Otro"]; // Solo básicas

function getTiposActividades(potreroTipo) {
  return potreroTipo === "libre" ? TIPOS_ACT_LIBRE : TIPOS_ACT_CULTIVADO;
}

function TabActividades({ pot, onUpdate, onConsumir, insumos=[], onEnfardar, user, profile }) {
  const [show,    setShow]    = useState(false);
  const [tipo,    setTipo]    = useState(pot.tipo==="libre" ? "Pastoreo" : "Siembra");
  const [fecha,   setFecha]   = useState(new Date().toISOString().slice(0,10));
  const [producto,setProducto]= useState("");
  const [dosis,   setDosis]   = useState("");
  const [unidad,  setUnidad]  = useState("kg/ha");
  const [horas,   setHoras]   = useState("");
  const [litros,  setLitros]  = useState("");
  const [costo,   setCosto]   = useState("");
  const [obs,     setObs]     = useState("");

  const tiposDisponibles = getTiposActividades(pot.tipo);
  const campos = CAMPOS_ACT[tipo] || [];
  const show_producto   = campos.includes("producto");
  const show_semilla    = campos.includes("semilla");
  const show_dosis      = campos.includes("dosis_agro");
  const show_horas      = campos.includes("horas");
  const show_litros     = campos.includes("litros");
  const show_costo      = campos.includes("costo");
  const show_fardo      = campos.includes("pasto_fardo");
  const show_cantFardos = campos.includes("cant_fardos");
  const show_tipoFardo  = campos.includes("tipo_fardo");

  // Costo automático desde el stock de insumos (gasoil + semilla)
  const litrosN = show_litros ? (parseFloat(litros)||0) : 0;
  const semKg = (show_semilla && producto && dosis)
    ? (unidad.includes("/ha") ? (parseFloat(dosis)||0)*(pot.ha||0) : (parseFloat(dosis)||0))
    : 0;
  const gasoilIt   = insumos.find(i=>i.categoria==="Gasoil");
  const semillaIt  = insumos.find(i=>i.categoria==="Semilla" && i.nombre===producto);
  const gasoilUnit  = gasoilIt?.costoUnit || 0;
  const semillaUnit = semillaIt?.costoUnit || 0;
  const costoAuto = Math.round(litrosN*gasoilUnit + semKg*semillaUnit);
  const autoCosto = show_litros || show_semilla; // estas actividades calculan el costo solas
  // No permitir consumir más de lo que hay en stock
  const stockError =
    (gasoilIt && litrosN > gasoilIt.cantidad) ? `Solo tenés ${gasoilIt.cantidad} L de gasoil en stock.` :
    (semillaIt && semKg > semillaIt.cantidad) ? `Solo tenés ${semillaIt.cantidad} kg de ${producto} en stock.` : "";

  const guardar = async () => {
    if (stockError) return;
    const act = {
      id:uid(), tipo, fecha, obs,
      producto: show_producto||show_semilla||show_fardo ? producto : "",
      dosis:    show_dosis||show_cantFardos ? dosis : "",
      unidad:   show_dosis ? unidad : show_tipoFardo ? unidad : "",
      horas:    show_horas ? parseFloat(horas)||0 : 0,
      litros:   litrosN,
      // Si consume stock, el costo se calcula solo; sino, manual
      costo:    autoCosto ? costoAuto : (show_costo ? parseFloat(costo)||0 : 0),
    };

    try {
      // Guardar actividad en tabla actividades
      const usuarioId = user?.id;
      const campoId = profile?.campo_propietario_id || user?.id;
      if (usuarioId && campoId && pot.id) {
        // Guardar actividad completa con todos los campos
        console.log("Guardando actividad:", { tipo, fecha, obs, producto: act.producto, dosis: act.dosis });
        const created = await actividadService.create(usuarioId, campoId, pot.id, {
          tipo,
          fecha,
          obs,
          producto: act.producto,
          dosis: act.dosis,
          unidad: act.unidad,
          horas: act.horas,
          litros: act.litros,
          costo: act.costo,
        });
        console.log("Actividad creada:", created);
        // Recargar actividades desde BD
        const actsFromBD = await actividadService.getByPotrero(pot.id);
        console.log("Actividades cargadas desde BD:", actsFromBD);
        // Convertir snake_case a camelCase
        const denormalized = (actsFromBD || []).map(a => denormalizeData(a));
        console.log("Actividades denormalizadas:", denormalized);
        onUpdate({ ...pot, actividades: denormalized });
      } else {
        onUpdate({ ...pot, actividades:[...(pot.actividades||[]),act] });
      }
    } catch (e) {
      console.error("Error guardando actividad en BD:", e);
    }

    // Descuento automático de insumos + registro de dónde se consumió
    if (onConsumir && (litrosN > 0 || semKg > 0)) {
      onConsumir({
        litros: litrosN,
        semillaNombre: semKg>0 ? producto : undefined,
        semillaKg: semKg,
        potreroNombre: pot.nombre,
        actividadTipo: tipo,
        fecha,
      });
    }

    // Si es "Enfardar", genera/suma los fardos en la sección Fardos
    if (tipo === "Enfardar" && onEnfardar) {
      onEnfardar({
        pasto: producto,
        tipoFardo: unidad,
        cantidad: parseInt(dosis) || 0,
        potreroId: pot.id,
        fecha,
      });
    }

    setShow(false); setProducto(""); setDosis(""); setHoras(""); setLitros(""); setCosto(""); setObs("");
  };

  const acts = pot.actividades || [];

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ fontSize:12, color:C.muted }}>{acts.length===0?"Sin actividades":`${acts.length} actividad${acts.length!==1?"es":""}`}</div>
        <Btn size="sm" variant="primary" onClick={()=>setShow(true)}>+ Registrar actividad</Btn>
      </div>

      {[...acts].reverse().map(a=>(
        <div key={a.id} style={{ background:C.arena, borderRadius:9, padding:"10px 12px", marginBottom:7 }}>
          <div style={{ display:"flex", justifyContent:"space-between" }}>
            <div>
              <span style={{ fontWeight:600, fontSize:13, color:C.tierra }}>{a.tipo}</span>
              {a.producto&&<span style={{ fontSize:12, color:C.muted, marginLeft:8 }}>{a.producto}</span>}
            </div>
            <span style={{ fontFamily:"monospace", fontSize:11, color:C.muted }}>{fmt(a.fecha)}</span>
          </div>
          <div style={{ display:"flex", gap:12, marginTop:6, flexWrap:"wrap" }}>
            {a.dosis&&<span style={{ fontSize:11, color:C.muted }}>Dosis/cant.: <b>{a.dosis} {a.unidad}</b></span>}
            {a.horas>0&&<span style={{ fontSize:11, color:C.muted }}>⏱ {a.horas}h</span>}
            {a.litros>0&&<span style={{ fontSize:11, color:C.muted }}>⛽ {a.litros}L</span>}
            {a.costo>0&&<span style={{ fontSize:11, color:C.muted }}>$ {a.costo}</span>}
          </div>
          {a.obs&&<div style={{ fontSize:11, color:C.muted, marginTop:4, fontStyle:"italic" }}>{a.obs}</div>}
        </div>
      ))}

      {show && (
        <div style={{ background:C.fondo, borderRadius:10, padding:16, border:`1px solid ${C.arenaDark}` }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
            <Select label="Tipo de actividad" value={tipo} onChange={v=>{setTipo(v);setProducto("");setDosis("");setHoras("");setLitros("");setCosto("");}} options={tiposDisponibles}/>
            <Input label="Fecha" value={fecha} onChange={setFecha} type="date"/>
          </div>

          {/* Campos condicionales según tipo */}
          {show_semilla && (
            <>
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:"0 12px" }}>
              <Select label="Semilla / variedad" value={producto} onChange={setProducto}
                options={[{v:"",l:"— Elegí semilla —"}, ...SEMILLAS_OPC.map(s=>{
                  const it = insumos.find(i=>i.categoria==="Semilla" && i.nombre===s);
                  const sinStock = !it || it.cantidad<=0;
                  return { v:s, l: it ? `${s} — ${it.cantidad} kg en stock` : `${s} — sin stock`, disabled: sinStock };
                })]}/>
              <Select label="Unidad" value={unidad} onChange={setUnidad} options={["kg/ha","g/ha","unidad/ha"]}/>
            </div>

            <Input label="Dosis" value={dosis} onChange={setDosis} type="number" placeholder="Ej: 80" required/>

            {/* Cálculo y validación de semilla */}
            {dosis && producto && (
              <div style={{ background: semKg > (semillaIt?.cantidad||0) ? "#FAE8E8" : "#E8F5E9",
                borderRadius:8, padding:"10px 12px", marginBottom:8, fontSize:11, color: semKg > (semillaIt?.cantidad||0) ? C.danger : "#2D5A34", fontWeight:500 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <span style={{fontWeight:600}}>{semKg.toFixed(1)} kg total</span>
                    <span style={{color:C.muted, fontSize:10, marginLeft:8}}>
                      ({dosis} {unidad.includes("/ha") ? `× ${pot.ha||0} ha` : ""})
                    </span>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:10, color:C.muted}}>Stock disponible:</div>
                    <span style={{fontWeight:600}}>{(semillaIt?.cantidad||0).toFixed(1)} kg</span>
                  </div>
                </div>
                {semKg > (semillaIt?.cantidad||0) && (
                  <div style={{marginTop:6, fontSize:10}}>
                    ⚠ Faltan {(semKg - (semillaIt?.cantidad||0)).toFixed(1)} kg
                  </div>
                )}
              </div>
            )}
            </>
          )}
          {show_producto && (
            <Input label="Producto" value={producto} onChange={setProducto} placeholder="Ej: Urea 46%, MCPA 40%..."/>
          )}
          {show_fardo && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
              <Select label="Tipo de pasto enfardado" value={producto} onChange={setProducto}
                options={[{v:"",l:"— Elegí pasto —"}, ...SEMILLAS_OPC.map(s=>({v:s,l:s}))]}/>
              <Select label="Tipo de fardo" value={unidad} onChange={setUnidad} options={["Redondo","Cuadrado","Megafardo"]}/>
            </div>
          )}
          {show_dosis && (
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:8 }}>
              <Input label="Dosis" value={dosis} onChange={setDosis} placeholder="Ej: 150"/>
              <Select label="Unidad" value={unidad} onChange={setUnidad} options={["kg/ha","L/ha","kg","L","unidad"]}/>
            </div>
          )}

          {show_dosis && dosis && unidad.includes("/ha") && (
            <div style={{ background:C.arena, borderRadius:8, padding:"8px 11px", marginBottom:10, fontSize:11, color:C.tierra }}>
              📐 <b>{(parseFloat(dosis)*pot.ha).toFixed(1)}</b> {unidad.replace("/ha","")} total
              <span style={{color:C.muted, marginLeft:6}}>({dosis} × {pot.ha} ha)</span>
            </div>
          )}
          {show_cantFardos && (
            <Input label="Cantidad de fardos" value={dosis} onChange={setDosis} type="number" placeholder="Ej: 80"/>
          )}

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
            {show_horas  && <Input label="Horas de trabajo" value={horas} onChange={setHoras} type="number" placeholder="0"/>}
            {show_litros && <Input label="Combustible (L)" value={litros} onChange={setLitros} type="number" placeholder="0"/>}
            {/* Costo manual solo si la actividad NO consume stock */}
            {show_costo && !autoCosto && <Input label="Costo total ($)" value={costo} onChange={setCosto} type="number" placeholder="0"/>}
          </div>

          {/* Costo automático calculado desde el stock */}
          {autoCosto && (
            <div style={{ background:C.arena, borderRadius:8, padding:"9px 12px", marginBottom:12, fontSize:12, color:C.tierra }}>
              💲 Costo calculado del stock: <b>${costoAuto.toLocaleString("es-UY")}</b>
              <div style={{ fontSize:10.5, color:C.muted, marginTop:2 }}>
                {litrosN>0 && `${litrosN}L gasoil`}{litrosN>0 && semKg>0 && " + "}{semKg>0 && `${semKg.toFixed(0)}kg semilla`}
                {(gasoilUnit===0 && litrosN>0) || (semillaUnit===0 && semKg>0)
                  ? " · ⚠ cargá el insumo con su costo en la sección Insumos para calcularlo" : " · según costo unitario del insumo"}
              </div>
            </div>
          )}

          <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2}/>
          {stockError && (
            <div style={{ background:"#FAE8E8", color:C.danger, padding:"8px 11px", borderRadius:8,
              fontSize:12, fontWeight:600, marginBottom:10 }}>⚠ {stockError} Cargá más en Insumos.</div>
          )}
          <div style={{ display:"flex", gap:8 }}>
            <Btn variant="ghost" size="sm" onClick={()=>setShow(false)} style={{flex:1}}>Cancelar</Btn>
            <Btn variant="primary" size="sm" onClick={guardar} disabled={!!stockError} style={{flex:1}}>Guardar actividad</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MODAL CARGA MASIVA ───────────────────────────────────
function ModalCargaMasiva({ pot, potreros, onSave, onCancel, user, profile }) {
  const [potreroId, setPotreroId] = useState(pot?.id || potreros?.[0]?.id || "");
  const [especie,   setEspecie]   = useState("Bovino");
  const [categoria, setCategoria] = useState("Vaca de cría");
  const [raza,      setRaza]      = useState("Hereford");
  const [sexo,      setSexo]      = useState("H");
  const [pesoBase,  setPesoBase]  = useState("");
  const [prefijo,   setPrefijo]   = useState("");
  const [numInicio, setNumInicio] = useState("1001");
  const [cantidad,  setCantidad]  = useState("10");

  const n      = Math.max(1, Math.min(200, parseInt(cantidad) || 1));
  const inicio = parseInt(numInicio) || 1;
  const caravanas = Array.from({ length: n }, (_, i) => `${prefijo}${inicio + i}`);
  const preview   = n <= 6 ? caravanas.join(", ")
    : `${caravanas.slice(0, 3).join(", ")} ... ${caravanas.at(-1)}`;

  const guardar = async () => {
    if (!potreroId) {
      alert("Selecciona un potrero");
      return;
    }
    if (caravanas.length === 0) {
      alert("Debe haber al menos 1 animal");
      return;
    }

    const kg  = parseFloat(pesoBase);
    const hoy = new Date().toISOString().slice(0, 10);
    const usuarioId = user?.id;
    const campoId = profile?.campo_propietario_id || user?.id;

    if (!usuarioId || !campoId) {
      console.error("No hay usuarioId o campoId");
      alert("Error: no está autenticado");
      return;
    }

    try {
      console.log(`Guardando ${caravanas.length} animales...`);

      // Guardar cada animal en BD (sin JSON anidados)
      const animalesGuardados = [];
      for (const caravana of caravanas) {
        try {
          const animalData = normalizeAnimal({
            caravana,
            especie,
            categoria,
            raza,
            sexo,
            peso_inicial: kg || 0,
            estado: "activo",
            potrero_id: potreroId,
            obs: "",
          });

          console.log(`Creando animal ${caravana}:`, animalData);

          const saved = await animalService.create(usuarioId, campoId, animalData);
          console.log(`✓ Animal ${caravana} creado:`, saved);

          // Si hay peso, guardar el pesaje en tabla pesadas
          if (kg > 0) {
            await pesadaService.create(usuarioId, saved.id, {
              fecha: hoy,
              kg,
            });
            console.log(`✓ Pesada registrada para ${caravana}`);
          }

          // Estructura completa para la UI
          animalesGuardados.push({
            ...saved,
            pesadas: kg ? [{ id: uid(), fecha: hoy, kg }] : [],
            eventos: [],
            movimientos: [],
            sanidad: [],
          });
        } catch (err) {
          console.error(`✗ Error creando animal ${caravana}:`, err);
          throw err; // Re-lanzar para detener el proceso
        }
      }

      console.log(`✓ Carga masiva completada: ${animalesGuardados.length} animales`);
      onSave(potreroId, animalesGuardados);
    } catch (e) {
      console.error("Error guardando animales en carga masiva:", e);
      alert(`Error: ${e.message || "No se pudieron guardar los animales"}`);
    }
  };

  return (
    <Modal title="Carga masiva de animales" onClose={onCancel} width={520}>
      {/* Selector de potrero solo si no hay pot fijo */}
      {!pot && potreros && (
        <Select label="Potrero destino" value={potreroId} onChange={setPotreroId} required
          options={potreros.map(p => ({ v: p.id, l: `${p.nombre} (${p.ha.toFixed(1)} ha)` }))}/>
      )}
      {pot && (
        <div style={{ background:C.arena, borderRadius:8, padding:"8px 12px", marginBottom:14, fontSize:12, color:C.muted }}>
          Potrero: <b style={{ color:C.tierra }}>{pot.nombre}</b>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
        <Select label="Especie" value={especie} onChange={v=>{setEspecie(v);setCategoria(catsDe(v)[0]);setRaza(RAZAS[v][0])}} options={ESPECIES}/>
        <Select label="Categoría" value={categoria} onChange={setCategoria} options={catsDe(especie)}/>
        <RazaInput label="Raza" value={raza} onChange={setRaza} especie={especie}/>
        <Select label="Sexo" value={sexo} onChange={setSexo} options={[{v:"H",l:"Hembra"},{v:"M",l:"Macho"}]}/>
        <Input label="Peso base (kg)" value={pesoBase} onChange={setPesoBase} type="number" placeholder="Opcional — aplica a todos"/>
      </div>

      <div style={{ borderTop:`1px solid ${C.arenaDark}`, marginTop:4, paddingTop:14, marginBottom:0 }}>
        <div style={{ fontSize:11, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:10 }}>
          Numeración de caravanas
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"0 12px" }}>
          <Input label="Prefijo" value={prefijo} onChange={setPrefijo} placeholder="Ej: A-"/>
          <Input label="N° inicial" value={numInicio} onChange={setNumInicio} type="number" placeholder="1001"/>
          <Input label="Cantidad" value={cantidad} onChange={setCantidad} type="number" placeholder="10"/>
        </div>
        {n > 0 && (
          <div style={{ background:C.arena, borderRadius:8, padding:"8px 12px", fontSize:12, color:C.muted, marginBottom:4 }}>
            <span style={{ fontWeight:600, color:C.texto }}>#{preview}</span>
            <span style={{ marginLeft:10, color:C.muted }}>— {n} animal{n!==1?"es":""}</span>
          </div>
        )}
      </div>

      <div style={{ display:"flex", gap:8, marginTop:14 }}>
        <Btn variant="ghost" onClick={onCancel} style={{flex:1}}>Cancelar</Btn>
        <Btn variant="primary" onClick={guardar} disabled={!potreroId||n<1} style={{flex:1}}>
          Agregar {n} animal{n!==1?"es":""}
        </Btn>
      </div>
    </Modal>
  );
}

// ─── TAB ANIMALES ─────────────────────────────────────────
function TabAnimales({ pot, onUpdate, potreros, onUpdateAnimal, onMoverAnimal, onVender, user, profile }) {
  const [show,       setShow]       = useState(false);
  const [showMasiva, setShowMasiva] = useState(false);
  const [fichaAnim,  setFichaAnim]  = useState(null);
  const [caravana,   setCaravana]   = useState("");
  const [categoria,  setCategoria]  = useState("Vaca de cría");
  const [especie,    setEspecie]    = useState("Bovino");
  const [raza,       setRaza]       = useState("Hereford");
  const [sexo,       setSexo]       = useState("H");
  const [peso,       setPeso]       = useState("");
  const [obs,        setObs]        = useState("");
  const [quitarAnim, setQuitarAnim] = useState(null); // {id, caravana}
  const [motivoQuitar, setMotivoQuitar] = useState("venta");
  const [fechaQuitar, setFechaQuitar] = useState(new Date().toISOString().slice(0,10));
  const [montoVenta, setMontoVenta] = useState("");
  const [potreroDestino, setPotreroDestino] = useState("");
  const [obsQuitar, setObsQuitar] = useState("");

  const guardar = async () => {
    const kg = parseFloat(peso);
    const hoy = new Date().toISOString().slice(0, 10);

    try {
      // Guardar animal en tabla animales (sin JSON anidados)
      const usuarioId = user?.id;
      const campoId = profile?.campo_propietario_id || user?.id;
      const saved = await animalService.create(usuarioId, campoId, normalizeAnimal({
        caravana,
        categoria,
        especie,
        raza,
        sexo,
        peso_inicial: kg || 0,
        estado: "activo",
        obs,
        potrero_id: pot.id,
      }));

      // Si hay peso, guardar el pesaje en tabla pesadas
      if (kg > 0) {
        await pesadaService.create(usuarioId, saved.id, {
          fecha: hoy,
          kg,
        });
      }

      // Actualizar estado local con estructura completa para la UI (convertir snake_case a camelCase)
      const nuevoAnimal = {
        ...denormalizeData(saved),
        pesadas: kg ? [{ id: uid(), fecha: hoy, kg }] : [],
        eventos: [],
        movimientos: [],
        sanidad: [],
      };
      onUpdate({ ...pot, animalesAsignados: [...(pot.animalesAsignados||[]), nuevoAnimal] });
      setShow(false); setCaravana(""); setPeso(""); setObs("");
    } catch (e) {
      console.error("Error guardando animal:", e);
    }
  };

  const abrirQuitarModal = (id) => {
    const animal = (pot.animalesAsignados || []).find(a => a.id === id);
    if (animal) {
      setQuitarAnim({ id, caravana: animal.caravana });
      setMotivoQuitar("venta");
      setFechaQuitar(new Date().toISOString().slice(0, 10));
      setMontoVenta("");
      setPotreroDestino("");
      setObsQuitar("");
    }
  };

  const confirmarQuitar = () => {
    if (!quitarAnim) return;
    if (motivoQuitar === "venta" && !montoVenta) return;
    if (motivoQuitar === "movimiento" && !potreroDestino) return;

    const nuevoAnimalesAsignados = (pot.animalesAsignados || []).filter(a => a.id !== quitarAnim.id);

    if (motivoQuitar === "venta") {
      // Registrar como vendido
      const monto = parseFloat(montoVenta) || 0;
      onVender && onVender(pot.id, { ...quitarAnim }, {
        fecha: fechaQuitar, pesoVenta: quitarAnim.pesoActual || 0, precioKg: montoVenta ? monto / (quitarAnim.pesoActual || 1) : 0, total: monto, comprador: obsQuitar || ""
      });
      onUpdate({ ...pot, animalesAsignados: nuevoAnimalesAsignados });
    } else if (motivoQuitar === "movimiento") {
      // Mover a otro potrero
      onMoverAnimal && onMoverAnimal(quitarAnim.id, pot.id, potreroDestino, {
        tipo: "movimiento", fecha: fechaQuitar, obs: obsQuitar || `Movido a otro potrero`
      });
      onUpdate({ ...pot, animalesAsignados: nuevoAnimalesAsignados });
    } else {
      // Muerte o retiro: solo eliminar
      onUpdate({ ...pot, animalesAsignados: nuevoAnimalesAsignados });
    }

    setQuitarAnim(null);
  };

  const animales = pot.animalesAsignados || [];

  const handleUpdateFicha = (updated) => {
    if (onUpdateAnimal) onUpdateAnimal(pot.id, updated);
    else onUpdate({ ...pot, animalesAsignados:pot.animalesAsignados.map(a=>a.id===updated.id?updated:a) });
    setFichaAnim(updated);
  };
  const handleMoverFicha = (animalId, fromId, toId, mov) => {
    if (onMoverAnimal) { onMoverAnimal(animalId, fromId, toId, mov); setFichaAnim(null); }
  };

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
        <StatCard val={animales.filter(a=>a.especie==="Bovino").length} label="bovinos"/>
        <StatCard val={animales.filter(a=>a.especie==="Ovino").length} label="ovinos"/>
      </div>

      <div style={{ display:"flex", gap:6, justifyContent:"flex-end", marginBottom:12 }}>
        <Btn size="sm" variant="secondary" onClick={()=>setShowMasiva(true)}>⚡ Carga masiva</Btn>
        <Btn size="sm" variant="primary"   onClick={()=>setShow(true)}>+ Uno a uno</Btn>
      </div>

      {animales.map(a=>(
        <div key={a.id} style={{ background:C.arena, borderRadius:8, padding:"8px 12px", marginBottom:5,
          display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <span style={{ fontFamily:"monospace", fontSize:12, fontWeight:600, color:C.tierra }}>#{a.caravana||"—"}</span>
            <span style={{ fontSize:12, color:C.muted, marginLeft:8 }}>{a.categoria}</span>
            {a.raza&&<span style={{ fontSize:11, color:C.muted, marginLeft:6 }}>· {a.raza}</span>}
            {pesoActual(a)>0&&<span style={{ fontSize:11, color:C.muted, marginLeft:6 }}>· {pesoActual(a)} kg</span>}
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <Btn size="sm" variant="secondary" onClick={()=>setFichaAnim(a)}>Ver ficha</Btn>
            <Btn size="sm" variant="danger"    onClick={()=>abrirQuitarModal(a.id)}>Quitar</Btn>
          </div>
        </div>
      ))}

      {show && (
        <div style={{ background:C.fondo, borderRadius:10, padding:16, border:`1px solid ${C.arenaDark}` }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
            <Select label="Especie" value={especie} onChange={v=>{setEspecie(v);setCategoria(catsDe(v)[0]);setRaza(RAZAS[v][0])}} options={ESPECIES}/>
            <Select label="Categoría" value={categoria} onChange={setCategoria} options={catsDe(especie)}/>
            <Input label="N° Caravana" value={caravana} onChange={setCaravana} placeholder="Ej: 1234"/>
            <Select label="Sexo" value={sexo} onChange={setSexo} options={[{v:"H",l:"Hembra"},{v:"M",l:"Macho"}]}/>
            <RazaInput label="Raza" value={raza} onChange={setRaza} especie={especie}/>
            <Input label="Peso actual (kg)" value={peso} onChange={setPeso} type="number" placeholder="Ej: 380"/>
          </div>
          <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2}/>
          <div style={{ display:"flex", gap:8 }}>
            <Btn variant="ghost" size="sm" onClick={()=>setShow(false)} style={{flex:1}}>Cancelar</Btn>
            <Btn variant="primary" size="sm" onClick={guardar} style={{flex:1}}>Agregar</Btn>
          </div>
        </div>
      )}

      {showMasiva && (
        <ModalCargaMasiva pot={pot}
          onSave={(_pid, animales) => {
            onUpdate({ ...pot, animalesAsignados:[...(pot.animalesAsignados||[]),...animales] });
            setShowMasiva(false);
          }}
          onCancel={()=>setShowMasiva(false)}
          user={user} profile={profile}/>
      )}

      {fichaAnim && (
        <FichaAnimal animal={fichaAnim} potreroId={pot.id} potreros={potreros||[pot]}
          onClose={()=>setFichaAnim(null)} onUpdate={handleUpdateFicha} onMover={handleMoverFicha}
          onVenta={(ventaData)=>{
            // Obtener animal actualizado del potrero para asegurar datos correctos
            const animalActualizado = (pot.animalesAsignados||[]).find(a=>a.id===fichaAnim.id);
            if (animalActualizado) venderAnimal(pot.id, animalActualizado, ventaData);
            setFichaAnim(null);
          }}
          onEliminar={()=>{ abrirQuitarModal(fichaAnim.id); setFichaAnim(null); }}/>
      )}

      {quitarAnim && (
        <Modal onClose={()=>setQuitarAnim(null)}>
          <div style={{ fontFamily:"serif", fontSize:18, fontWeight:600, color:C.tierra, marginBottom:16 }}>
            Quitar animal #{quitarAnim.caravana||"—"}
          </div>

          <Select label="¿Qué sucedió?" value={motivoQuitar} onChange={setMotivoQuitar}
            options={[
              {v:"venta", l:"Venta"},
              {v:"movimiento", l:"Movimiento a otro potrero"},
              {v:"muerte", l:"Muerte"},
              {v:"retiro", l:"Retiro"}
            ]}/>

          <Input label="Fecha" value={fechaQuitar} onChange={setFechaQuitar} type="date"/>

          {motivoQuitar === "venta" && (
            <>
              <Input label="Monto de venta ($)" value={montoVenta} onChange={setMontoVenta} type="number" placeholder="0" required/>
              <Textarea label="Notas (opcional)" value={obsQuitar} onChange={setObsQuitar} rows={2} placeholder="Ej: Vendido a frigorífico"/>
            </>
          )}

          {motivoQuitar === "movimiento" && (
            <>
              <Select label="Potrero destino" value={potreroDestino} onChange={setPotreroDestino}
                options={[{v:"",l:"— Seleccionar —"}, ...(potreros||[]).filter(p=>p.id!==pot.id).map(p=>({v:p.id,l:p.nombre}))]}
                required/>
              <Textarea label="Notas (opcional)" value={obsQuitar} onChange={setObsQuitar} rows={2}/>
            </>
          )}

          {(motivoQuitar === "muerte" || motivoQuitar === "retiro") && (
            <Textarea label="Notas (opcional)" value={obsQuitar} onChange={setObsQuitar} rows={2} placeholder="Ej: Causa de la muerte"/>
          )}

          <div style={{ display:"flex", gap:8, marginTop:16 }}>
            <Btn variant="ghost" onClick={()=>setQuitarAnim(null)} style={{flex:1}}>Cancelar</Btn>
            <Btn variant="danger" onClick={confirmarQuitar} disabled={
              (motivoQuitar==="venta" && !montoVenta) || (motivoQuitar==="movimiento" && !potreroDestino)
            } style={{flex:1}}>
              Confirmar
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function FichaPotrero({ pot, potreros, onClose, onUpdate, onDelete, onEditShape, onConsumir, insumos, onEnfardar, onUpdateAnimal, onMoverAnimal, onVender, user, profile }) {
  const [tab, setTab] = useState("info");
  const tabs = [{ id:"info",label:"Info" },{ id:"actividades",label:"Actividades" },{ id:"animales",label:"Animales" }];
  return (
    <Modal title={pot.nombre} onClose={onClose} width={520}>
      {onEditShape && (
        <Btn variant="secondary" onClick={onEditShape} style={{ width:"100%", marginBottom:12 }}>
          ✏️ Editar forma en el mapa
        </Btn>
      )}
      <Tabs tabs={tabs} active={tab} onChange={setTab}/>
      {tab==="info"        && <TabInfo pot={pot} onUpdate={onUpdate} onDelete={onDelete} onClose={onClose}/>}
      {tab==="actividades" && <TabActividades pot={pot} onUpdate={onUpdate} onConsumir={onConsumir} insumos={insumos} onEnfardar={onEnfardar} user={user} profile={profile}/>}
      {tab==="animales"    && <TabAnimales pot={pot} onUpdate={onUpdate} potreros={potreros} onUpdateAnimal={onUpdateAnimal} onMoverAnimal={onMoverAnimal} onVender={onVender} user={user} profile={profile}/>}
    </Modal>
  );
}

// ─── HELPERS PARA EL RESUMEN DEL AGENTE ───────────────────
// Unidades Ganaderas por animal (aproximación uruguaya)
function ugAnimal(a) {
  const cat = (a.categoria || "").toLowerCase();
  if (a.especie === "Ovino")   return cat.includes("cordero") ? 0.1 : 0.15;
  if (a.especie === "Caballo") return 1.2;
  if (cat.includes("ternero")) return 0.6;
  if (cat.includes("novillito") || cat.includes("vaquillona")) return 0.8;
  if (cat.includes("toro"))    return 1.3;
  return 1; // vaca, novillo, buey adulto
}
const estacionUY = (d = new Date()) => {
  const m = d.getMonth() + 1;
  return (m === 12 || m <= 2) ? "verano" : m <= 5 ? "otoño" : m <= 8 ? "invierno" : "primavera";
};
const diasDesde = (fechaISO) => {
  if (!fechaISO) return null;
  return Math.floor((Date.now() - new Date(fechaISO + "T12:00:00").getTime()) / 86400000);
};

// ─── CONFIG ASESOR IA ─────────────────────────────────────
// Límite de uso. Por ahora APAGADO para pruebas. Para activarlo: limiteActivo:true
const IA_CONFIG = {
  limiteActivo: false,   // ⬅️ poné true para activar el límite de uso
  cadaHoras: 168,        // 168 = 1 vez por semana (poné 24 para 1 vez por día)
};

// El system prompt del Asesor IA vive en la Edge Function asesor-ia (servidor).

// ─── PANEL IA ─────────────────────────────────────────────
// Formatea la respuesta del Asesor IA (markdown-lite → estilos, incluye tablas)
function inlineMd(s) {
  return s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} style={{ color: C.tierra }}>{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>
  );
}

function TablaMd({ rows }) {
  const [head, ...body] = rows;
  return (
    <div style={{ overflowX: "auto", margin: "6px 0 10px" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
        <thead>
          <tr>
            {head.map((c, i) => (
              <th key={i} style={{ textAlign: "left", padding: "7px 10px", background: C.tierra, color: "#fff",
                fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, ri) => (
            <tr key={ri} style={{ background: ri % 2 ? C.arena : C.white }}>
              {r.map((c, ci) => (
                <td key={ci} style={{ padding: "7px 10px", borderBottom: `1px solid ${C.arenaDark}`,
                  color: C.texto, verticalAlign: "top", fontWeight: ci === 0 ? 600 : 400,
                  whiteSpace: ci === 0 ? "nowrap" : "normal" }}>{inlineMd(c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlanIAFormat({ text }) {
  // Agrupa líneas en bloques (tablas vs líneas sueltas)
  const lines = (text || "").split("\n");
  const blocks = [];
  let tbl = null;
  const flush = () => { if (tbl && tbl.length) blocks.push({ t: "table", rows: tbl }); tbl = null; };
  lines.forEach((raw) => {
    const ln = raw.trim();
    if (ln.startsWith("|") && ln.endsWith("|")) {
      const cells = ln.slice(1, -1).split("|").map(c => c.trim());
      if (cells.every(c => /^:?-{2,}:?$/.test(c) || c === "")) return; // separador
      (tbl = tbl || []).push(cells);
      return;
    }
    flush();
    blocks.push({ t: "line", ln });
  });
  flush();

  return (
    <div style={{ fontSize: 13.5, lineHeight: 1.7, color: C.texto }}>
      {blocks.map((b, i) => {
        if (b.t === "table") return <TablaMd key={i} rows={b.rows} />;
        const ln = b.ln;
        if (!ln) return <div key={i} style={{ height: 7 }} />;
        const head = ln.match(/^\*\*(.+?)\*\*:?$/);
        if (head) return (
          <div key={i} style={{ fontSize: 15, fontWeight: 700, color: C.tierra,
            marginTop: i ? 16 : 0, marginBottom: 7, paddingBottom: 5, borderBottom: `2px solid ${C.pastoLight}` }}>
            {head[1]}
          </div>
        );
        const bullet = ln.match(/^[-*•·]\s+(.+)/);
        if (bullet) return (
          <div key={i} style={{ display: "flex", gap: 8, margin: "4px 0 4px 4px" }}>
            <span style={{ color: C.pasto, flexShrink: 0, fontWeight: 700 }}>•</span>
            <span>{inlineMd(bullet[1])}</span>
          </div>
        );
        const num = ln.match(/^(\d+)[.)]\s+(.+)/);
        if (num) return (
          <div key={i} style={{ display: "flex", gap: 8, margin: "4px 0 4px 4px" }}>
            <span style={{ color: C.pasto, fontWeight: 700, flexShrink: 0 }}>{num[1]}.</span>
            <span>{inlineMd(num[2])}</span>
          </div>
        );
        return <div key={i} style={{ margin: "4px 0" }}>{inlineMd(ln)}</div>;
      })}
    </div>
  );
}

function PanelIA({ potreros, transacciones=[], precios, ubicacion, onClose }) {
  // El último plan queda guardado para releerlo sin regenerar
  const [plan,    setPlan]    = useState(() => {
    try { const s = localStorage.getItem("campoIA_iaPlan"); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const ultimoUso = () => {
    const t = localStorage.getItem("campoIA_iaLast");
    return t ? Number(t) : 0;
  };
  const bloqueadoHasta = IA_CONFIG.limiteActivo
    ? ultimoUso() + IA_CONFIG.cadaHoras * 3600 * 1000
    : 0;
  const limitado = Date.now() < bloqueadoHasta;

  const generar = async () => {
    setLoading(true); setPlan(null); setError("");
    const totalAnim = potreros.reduce((s,p)=>s+(p.animalesAsignados||[]).length, 0);

    // Resumen compacto: agregados y métricas derivadas (no datos crudos)
    let totalUG = 0, totalHa = 0;
    const lineas = potreros.map(p=>{
      const anim = p.animalesAsignados||[];
      const ug = anim.reduce((s,a)=>s+ugAnimal(a), 0);
      totalUG += ug; totalHa += p.ha||0;
      const porEsp = {};
      anim.forEach(a=>{ porEsp[a.especie]=(porEsp[a.especie]||0)+1; });
      const animStr = Object.entries(porEsp).map(([e,n])=>`${n} ${e.toLowerCase()}${n>1?"s":""}`).join(", ") || "vacío";
      const carga = p.ha ? (ug/p.ha).toFixed(2) : "0";
      const dAct = diasDesde((p.actividades||[]).at(-1)?.fecha);
      const desc = anim.length===0
        ? (dAct!=null ? `${dAct}d desde últ. actividad` : "sin uso reciente")
        : (dAct!=null ? `${dAct}d sin rotar` : "");
      const fha = (h)=>(h||0).toLocaleString("es-UY",{maximumFractionDigits:2});
      return `- ${p.nombre}: ${fha(p.ha)}ha, ${p.pasto}, ${ESTADO_LABEL[p.estado]} · ${animStr}${ug?` (${ug.toFixed(0)} UG, ${carga} UG/ha)`:""}${desc?` · ${desc}`:""}`;
    }).join("\n");
    const cargaGlobal = totalHa ? (totalUG/totalHa).toFixed(2) : "0";

    // Fechas reales (para que la IA no invente días)
    const diasFull = ["domingo","lunes","martes","miércoles","jueves","viernes","sábado"];
    const hoyD = new Date();
    const dd = (d)=>`${diasFull[d.getDay()]} ${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`;
    const fechasSemana = Array.from({length:7},(_,k)=>{ const d=new Date(hoyD); d.setDate(hoyD.getDate()+k); return dd(d); });

    // Economía del mes actual
    const mes = new Date().toISOString().slice(0,7);
    const txMes = (transacciones||[]).filter(t=>(t.fecha||"").slice(0,7)===mes);
    const ing = txMes.filter(t=>t.tipo==="ingreso").reduce((s,t)=>s+(t.monto||0),0);
    const egr = txMes.filter(t=>t.tipo==="egreso").reduce((s,t)=>s+(t.monto||0),0);

    // Precios de hacienda (los más relevantes)
    const preciosStr = (precios?.items||[]).slice(0,4).map(p=>`${p.label} ${p.val}`).join(" · ");

    const resumen = `FECHA: hoy es ${dd(hoyD)}/${hoyD.getFullYear()} (${estacionUY()}).
Los 7 días del plan (usá EXACTAMENTE estas fechas, NO inventes): ${fechasSemana.join(" · ")}.

CAMPO · ${ubicacion||"Uruguay"}
${totalHa.toLocaleString("es-UY",{maximumFractionDigits:2})} ha · ${totalUG.toFixed(0)} UG · carga global ${cargaGlobal} UG/ha

POTREROS:
${lineas}

ECONOMÍA (mes en curso): ingresos $${ing.toLocaleString("es-UY")}, egresos $${egr.toLocaleString("es-UY")}, resultado $${(ing-egr).toLocaleString("es-UY")}
PRECIOS HACIENDA (USD/kg): ${preciosStr || "s/d"}`;

    try {
      // La generación IA SIEMPRE pasa por la Edge Function (la API key vive en el
      // servidor). Nunca se llama a la API de Anthropic directo desde el browser:
      // cualquier key embebida en el bundle quedaría pública.
      const text = await iaService.generar({ resumen, totalAnim, nPotreros: potreros.length });
      const nuevo = { text, ts: Date.now() };
      setPlan(nuevo);
      localStorage.setItem("campoIA_iaLast", String(Date.now()));
      try { localStorage.setItem("campoIA_iaPlan", JSON.stringify(nuevo)); } catch {}
    } catch(e) {
      setError(e.message || "No se pudo generar el plan.");
    }
    setLoading(false);
  };

  return (
    <Modal title="✦ Asesor de CampoIA" onClose={onClose} width={760}>
      {!plan && !loading && (
        <div>
          {/* Intro marketinera */}
          <div style={{ display:"flex", gap:14, alignItems:"flex-start", marginBottom:18 }}>
            <div style={{ fontSize:40, flexShrink:0 }}>🧑‍🌾</div>
            <div>
              <div style={{ fontSize:16, fontWeight:700, color:C.tierra, marginBottom:6 }}>
                Tu agrónomo virtual, 24/7
              </div>
              <p style={{ fontSize:13, color:C.texto, lineHeight:1.6, margin:0 }}>
                Nuestro agente especializado analiza el estado real de tu campo —potreros,
                pasturas, animales y actividades— y te arma un <b>plan de manejo para la semana</b>:
                qué potrero pastorear cada día, alertas sanitarias y la jugada estratégica del mes. 🌱
              </p>
            </div>
          </div>

          <div style={{ background:C.pastoLight+"18", border:`1px solid ${C.pastoLight}55`,
            borderRadius:10, padding:"10px 14px", marginBottom:18, fontSize:12, color:C.tierra, lineHeight:1.6 }}>
            🔮 <b>Pronostica 1 semana a futuro.</b><br/>
            {IA_CONFIG.limiteActivo
              ? `⏳ Uso limitado a 1 consulta cada ${IA_CONFIG.cadaHoras>=168?"semana":"día"}.`
              : "✨ Modo de pruebas: sin límite de consultas."}
          </div>

          {limitado ? (
            <div style={{ background:"#FFF8E8", border:"1px solid #F0C060", borderRadius:10,
              padding:"12px 14px", fontSize:13, color:"#7A5800", lineHeight:1.6 }}>
              Ya generaste tu consulta de este período. Volvé el {new Date(bloqueadoHasta).toLocaleDateString("es-UY")} para una nueva. 🌾
            </div>
          ) : (
            <Btn variant="primary" onClick={generar} disabled={potreros.length===0}
              style={{width:"100%",padding:13,fontSize:15}}>
              ✦ Generar mi plan de la semana
            </Btn>
          )}
          {potreros.length===0 && (
            <div style={{ fontSize:11, color:C.muted, marginTop:8, textAlign:"center" }}>
              Necesitás al menos un potrero cargado.
            </div>
          )}
        </div>
      )}
      {loading && (
        <div style={{ textAlign:"center", padding:40, color:C.muted }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🌱</div>
          <div style={{ fontSize:13 }}>El asesor está analizando tu campo...</div>
        </div>
      )}
      {error && !loading && (
        <div style={{ background:"#FAE8E8", color:C.danger, padding:"12px 14px", borderRadius:10,
          fontSize:13, marginTop:14, lineHeight:1.5 }}>
          {error}
          <Btn variant="ghost" size="sm" onClick={()=>setError("")} style={{ marginTop:8 }}>Reintentar</Btn>
        </div>
      )}
      {plan && !loading && (
        <div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, gap:8, flexWrap:"wrap" }}>
            <span style={{ fontSize:11, color:C.muted, fontFamily:"monospace" }}>
              🗓 Generado el {new Date(plan.ts).toLocaleString("es-UY", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" })}
            </span>
          </div>
          <div style={{ background:C.white, border:`1px solid ${C.arenaDark}`, borderRadius:12, padding:"16px 18px",
            maxHeight:440, overflowY:"auto" }}>
            <PlanIAFormat text={plan.text}/>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:14 }}>
            {!limitado && (
              <Btn variant="secondary" onClick={generar} disabled={potreros.length===0} style={{flex:1}}>
                🔄 Generar de nuevo
              </Btn>
            )}
            <Btn variant="ghost" onClick={onClose} style={{flex:1}}>Cerrar</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── APP PRINCIPAL ────────────────────────────────────────
export default function CampoIA({ user, profile, onProfileUpdate, LogoutButton, camposDisponibles, campoActivo, onCampoChange, onCampoCreado }) {
  const isMobile = useIsMobile();
  const [showSidebar, setShowSidebar] = useState(false); // drawer del panel en mobile
  const [avisoParaAgenda, setAvisoParaAgenda] = useState(null); // aviso de Home clickeado para crear tarea en Agenda
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const s = typeof localStorage!=="undefined" && localStorage.getItem("campoIA_sidebarW");
    return s ? +s : 300;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const c = typeof localStorage!=="undefined" && localStorage.getItem("campoIA_sidebarCollapsed");
    return c === "true";
  });
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    try { localStorage.setItem("campoIA_sidebarCollapsed", String(newState)); } catch {}
  };

  // Arrastre para redimensionar el sidebar del mapa
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = sidebarWidth;
    const onMove = (ev) => {
      const w = Math.min(560, Math.max(220, startW + (ev.clientX - startX)));
      setSidebarWidth(w);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = ""; document.body.style.userSelect = "";
      try { localStorage.setItem("campoIA_sidebarW", String(sidebarWidthRef.current)); } catch {}
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize"; document.body.style.userSelect = "none";
  };
  const sidebarWidthRef = useRef(sidebarWidth);
  sidebarWidthRef.current = sidebarWidth;

  // Ref para evitar cargar animales dos veces cuando los potreros se actualizan
  const lastProcessedCampoIdRef = useRef(null);

  // Estados principales - usando hooks
  // Los DATOS se consultan con el id del DUEÑO del campo (para campos compartidos,
  // el dueño; para campos propios, uno mismo). Así un usuario con acceso ve el
  // contenido del campo del dueño.
  const dataUserId = profile?.campo_owner_id || user?.id;
  const potreroState = usePotrero(dataUserId, profile?.campo_propietario_id || user?.id);
  const { potreros, setPotreros } = potreroState;

  const [drawing,       setDrawing]       = useState(false);
  const [currentPts,    setCurrentPts]    = useState([]);
  const [pendingPts,    setPendingPts]    = useState(null);
  const [selected,      setSelected]      = useState(null);
  const [showFicha,     setShowFicha]     = useState(false);
  const [editandoForma, setEditandoForma] = useState(null);
  const [formaOriginal, setFormaOriginal] = useState(null);
  const [showIA,        setShowIA]        = useState(false);
  const [activeNav,     setActiveNav]     = useState("inicio");
  const [mostrarMas,    setMostrarMas]    = useState(false); // sheet de navegación en mobile
  const [showBuscar,    setShowBuscar]    = useState(false); // búsqueda global

  // Sanidad, economía, fardos - usando hooks
  const sanidadState = useSanidad(dataUserId, profile?.campo_propietario_id || user?.id);
  const { stock: stockSanidad, setStock: setStockSanidad } = sanidadState;

  const economiaState = useEconomia(dataUserId, profile?.campo_propietario_id || user?.id);
  const { transacciones, setTransacciones } = economiaState;

  const fardosState = useFardos(dataUserId, profile?.campo_propietario_id || user?.id);
  const { fardos, setFardos } = fardosState;

  // Todos los estados ahora vienen de BD via hooks
  const tareasState = useTareas(dataUserId, profile?.campo_propietario_id || user?.id);
  const { tareas, setTareas } = tareasState;

  const empleadosState = useEmpleados(dataUserId, profile?.campo_propietario_id || user?.id);
  const { empleados, setEmpleados } = empleadosState;

  const maquinariasState = useMaquinarias(dataUserId, profile?.campo_propietario_id || user?.id);
  const { maquinarias, setMaquinarias } = maquinariasState;

  const zonasState = useZonas(dataUserId, profile?.campo_propietario_id || user?.id);
  const { zonas, setZonas } = zonasState;

  const insumosState = useInsumos(dataUserId, profile?.campo_propietario_id || user?.id);
  const { insumos, setInsumos } = insumosState;

  const [features,      setFeatures]      = useState(profile?.features || {});
  const [vendidos,      setVendidos]      = useState([]);
  const [pesadasCampo,  setPesadasCampo]  = useState([]); // análisis de peso (todo el campo)
  const [eventosCampo,  setEventosCampo]  = useState([]); // eventos reproductivos (todo el campo)

  // Crear nuevo campo (con su propio campo_id, sin sobrescribir el actual)
  const crearNuevoCampo = async () => {
    if (!nombreNuevoCampo.trim()) return;

    try {
      const nuevoCampoId = crypto.randomUUID();

      // Nueva fila en campos_metadata (user.id + campo_id nuevo)
      await estadoService.guardar(
        { nombreCampo: nombreNuevoCampo, ubicacion: ubicacionNuevoCampo, features: features || {} },
        user?.id, nuevoCampoId
      );

      const nombreCreado = nombreNuevoCampo;

      // Resetear form + cerrar modal
      setNombreNuevoCampo("");
      setUbicacionNuevoCampo("");
      setMostrarCrearCampo(false);

      // Cambiar al nuevo campo (dispara la recarga de datos vacíos) y refrescar la lista
      if (onCampoChange) onCampoChange({ id: nuevoCampoId, nombre: nombreCreado, propietario: user?.id, esPropio: true });
      if (onCampoCreado) onCampoCreado();
      toast.success(`Campo "${nombreCreado}" creado.`);
    } catch (e) {
      console.error("Error creando campo:", e);
      toast.error("No se pudo crear el campo: " + (e.message || "error"));
    }
  };

  // Cargar features del permisos_campos si es campo compartido
  useEffect(() => {
    const cargarFeaturesAcceso = async () => {
      // Compartido = no es propio (un campo propio con otro campo_id NO es compartido)
      const esCampoCompartido = profile?.campo_es_propio === false;

      if (!esCampoCompartido) {
        setFeatures(profile?.features || {});
        return;
      }

      // Si las features ya vienen en el profile (desde AppWithAuth), usarlas
      if (profile?.features_acceso) {
        setFeatures(profile.features_acceso);
        return;
      }

      // Fallback: cargar desde BD
      try {
        // Puede haber más de una fila para el mismo par (propietario, usuario) si
        // el acceso se asignó dos veces desde Admin. Con .single() eso devolvía 406
        // y caíamos en setFeatures({}), que por convención significa "todo
        // habilitado" (ver isFeatureOn) — o sea, las restricciones del dueño se
        // ignoraban en silencio. Tomamos el permiso más reciente.
        const { data, error } = await supabase
          .from("permisos_campos")
          .select("features")
          .eq("usuario_propietario_id", profile.campo_propietario_id)
          .eq("usuario_acceso_id", user?.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error || !data) {
          setFeatures({}); // Sin acceso = sin features
        } else {
          setFeatures(data.features || {});
        }
      } catch (e) {
        console.error("Error cargando features del acceso:", e);
        setFeatures({});
      }
    };

    cargarFeaturesAcceso();
  }, [profile?.campo_propietario_id, profile?.features_acceso, user?.id]);
  const [clima,         setClima]         = useState(CLIMA_MOCK);       // general (litoral oeste UY)
  const [climaPotreros, setClimaPotreros] = useState(null);             // centrado en los potreros
  const [alertasClima,  setAlertasClima]  = useState([]);              // derivadas del pronóstico real
  const [precios,       setPrecios]       = useState(PRECIOS_REF);      // precios ACG (fallback estático)
  const [semillas,      setSemillas]      = useState(SEMILLAS_REF);     // precios semillas ANAPROSE
  const [nombreCampo,   setNombreCampo]   = useState(profile?.nombre_campo || "Mi Campo");
  const [ubicacion,     setUbicacion]     = useState(profile?.ubicacion || "");
  const [editNombre,    setEditNombre]    = useState(false);
  const [mostrarCrearCampo, setMostrarCrearCampo] = useState(false);
  const [nombreNuevoCampo, setNombreNuevoCampo] = useState("");
  const [ubicacionNuevoCampo, setUbicacionNuevoCampo] = useState("");
  const [datosListos,   setDatosListos]   = useState(false);
  const [showMisDatos,  setShowMisDatos]  = useState(false);
  const [syncStatus,    setSyncStatus]    = useState("idle"); // idle | saving | saved | error

  // H6: CARGA inicial desde Supabase - simplificada con hooks
  useEffect(() => {
    let cancel = false;
    const usuarioId = dataUserId;
    const campoId = profile?.campo_propietario_id || user?.id;
    if (!usuarioId || !campoId) return;

    (async () => {
      try {
        // Cargar metadata del campo
        const metadata = await estadoService.cargar(usuarioId, campoId);
        if (!cancel && metadata) {
          setNombreCampo(metadata.nombreCampo || "Mi Campo");
          setUbicacion(metadata.ubicacion || "");
          if (metadata.features) setFeatures(metadata.features);
        }

        // Potreros, transacciones, stock, fardos, insumos, tareas, zonas, empleados, maquinarias
        // se cargan vía hooks automáticamente - no resetear aquí
        // Cargar animales vendidos (persisten con estado='vendido')
        const [vendidosDB, detallesVendidos] = await Promise.all([
          animalService.getVendidos(usuarioId, campoId),
          animalService.getDetallesPorCampo(usuarioId, campoId),
        ]);
        if (!cancel) {
          setVendidos((vendidosDB || []).map(a => {
            const h = hidratarAnimal(a, detallesVendidos);
            return { ...h, potreroOrigen: h.potreroOrigen || "" };
          }));
        }

      } catch (e) {
        console.error("Error cargando datos:", e);
      } finally {
        if (!cancel) setDatosListos(true);
      }
    })();

    return () => { cancel = true; };
  }, [profile?.campo_propietario_id, user?.id]);

  // H6: GUARDADO automático - metadata en app_state (debounce 800ms)
  useEffect(() => {
    if (!datosListos || !user?.id) return;
    // No guardar metadata si es un campo compartido (solo el dueño la edita)
    if (profile?.campo_es_propio === false) return;
    setSyncStatus("saving");
    const t = setTimeout(() => {
      const usuarioId = dataUserId;
      const campoId = profile?.campo_propietario_id || user?.id;

      // Solo guardar metadata
      estadoService.guardar({ nombreCampo, ubicacion, features }, usuarioId, campoId)
        .then(() => setSyncStatus("saved"))
        .catch((e) => { console.error("❌ Error guardando metadata:", e); setSyncStatus("error"); });
    }, 800);
    return () => clearTimeout(t);
  }, [datosListos, nombreCampo, ubicacion, features, user?.id, profile?.campo_propietario_id]);

  // H7: CARGAR ANIMALES desde tabla cuando potreros cambian (refactorizado con hooks)
  useEffect(() => {
    if (!datosListos || !user?.id || potreros.length === 0) return;

    // Guard: si los potreros no pertenecen al campo actual, no hacer nada todavía.
    // Esto ocurre cuando cambias de campo: usePotrero vacía primero, luego carga nuevos.
    // Sin este guard, cargamos animales del campo anterior mientras esperamos que
    // usePotrero traiga los del nuevo.
    const campoIdEsperado = profile?.campo_propietario_id || user?.id;
    if (potreros.length > 0 && potreros[0].campo_id !== campoIdEsperado) {
      // Los potreros en memoria son del campo anterior, esperar a que se carguen los nuevos
      return;
    }

    // Evitar procesar el mismo campo dos veces (cuando se actualiza potreros con animales)
    if (lastProcessedCampoIdRef.current === campoIdEsperado) {
      return;
    }
    lastProcessedCampoIdRef.current = campoIdEsperado;

    // `cancel` evita reescribir el estado si el campo cambió mientras cargábamos:
    // este efecto mapea sobre los potreros que hay AHORA en memoria, que al cambiar
    // de campo son todavía los del campo anterior. Sin el guard, los volvía a
    // guardar y el campo nuevo quedaba mostrando los potreros del viejo.
    let cancel = false;
    const potrerosDelCampo = potreros;

    (async () => {
      const usuarioId = dataUserId;
      const campoId = profile?.campo_propietario_id || user?.id;

      // Sub-colecciones del campo (pesadas, eventos, sanidad, movimientos) en 4
      // queries, para hidratar cada animal en el mismo formato que tiene en memoria
      const detalles = await animalService.getDetallesPorCampo(usuarioId, campoId);
      if (cancel) return;

      // Para cada potrero, cargar animales desde tabla
      const potrerosConAnimales = await Promise.all(
        potrerosDelCampo.map(async (pot) => {
          try {
            const animales = await animalService.getByPotrero(usuarioId, campoId, pot.id);
            return { ...pot, animalesAsignados: (animales || []).map(a => hidratarAnimal(a, detalles)) };
          } catch {
            return pot;
          }
        })
      );

      if (!cancel) setPotreros(potrerosConAnimales);
    })();

    return () => { cancel = true; };
  }, [datosListos, user?.id, dataUserId, profile?.campo_propietario_id, potreros.length]);

  // Cargar pesadas y eventos reproductivos del campo (para la sección Reproducción)
  useEffect(() => {
    if (!datosListos) return;
    const cId = profile?.campo_propietario_id || user?.id;
    if (!dataUserId || !cId) return;
    let cancel = false;
    (async () => {
      try {
        const [ps, ev] = await Promise.all([
          pesadaService.getByCampo(dataUserId, cId),
          eventoReproduccionService.getByCampo(dataUserId, cId),
        ]);
        if (!cancel) { setPesadasCampo(ps || []); setEventosCampo(ev || []); }
      } catch (e) { console.error("Error cargando reproducción/peso:", e); }
    })();
    return () => { cancel = true; };
  }, [datosListos, dataUserId, profile?.campo_propietario_id, user?.id]);

  const addEventoRepro = async (animalId, evento) => {
    await eventoReproduccionService.create(dataUserId, animalId, evento);
    const cId = profile?.campo_propietario_id || user?.id;
    const ev = await eventoReproduccionService.getByCampo(dataUserId, cId);
    setEventosCampo(ev || []);
  };

  // Centro geográfico de todos los potreros (promedio de sus vértices)
  const centroPotreros = (() => {
    const pts = potreros.flatMap(p => p.points || []);
    if (!pts.length) return null;
    const lat = pts.reduce((s,p)=>s+p[0],0)/pts.length;
    const lng = pts.reduce((s,p)=>s+p[1],0)/pts.length;
    return [lat, lng];
  })();

  // Helper: parsea respuesta OpenMeteo a objetos con temp y lluvia
  const fetchClima = (lat, lng) =>
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,temperature_2m_min,weathercode,precipitation_sum,precipitation_probability_max&timezone=America%2FMontevideo&forecast_days=7`)
      .then(r=>r.json())
      .then(d=>d.daily.time.map((f,i)=>({
        ic: wxIcon(d.daily.weathercode[i]),
        dia: DIAS[new Date(f+"T12:00:00").getDay()],
        max: Math.round(d.daily.temperature_2m_max[i]),
        min: Math.round(d.daily.temperature_2m_min[i]),
        lluvia: Math.round(d.daily.precipitation_sum[i] || 0),
        prob: d.daily.precipitation_probability_max[i] ?? 0,
      })));

  // H5: clima general (litoral oeste UY)
  useEffect(() => {
    fetchClima(-33.5, -57.9).then(setClima).catch(()=>{});
  }, []);

  // Precios ACG en vivo (Edge Function). Si no está desplegada, quedan los estáticos.
  useEffect(() => {
    preciosService.obtener()
      .then((p) => { if (p?.items?.length) setPrecios(p); })
      .catch(()=>{});
    semillasService.obtener()
      .then((s) => { if (s?.items?.length) setSemillas(s); })
      .catch(()=>{});
  }, []);

  // Clima + alertas reales centrados en los potreros del usuario
  useEffect(() => {
    if (!centroPotreros) { setClimaPotreros(null); setAlertasClima([]); return; }
    const [lat, lng] = centroPotreros;
    fetchClima(lat, lng).then(setClimaPotreros).catch(()=>{});
    // Alertas derivadas del pronóstico (heladas, lluvias, calor)
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_min,temperature_2m_max,precipitation_sum&timezone=America%2FMontevideo&forecast_days=7`)
      .then(r=>r.json())
      .then(d=>{
        const dd = d.daily, al = [];
        const fday = (i)=>DIAS[new Date(dd.time[i]+"T12:00:00").getDay()];
        const heladas = dd.time.map((_,i)=>i).filter(i=>dd.temperature_2m_min[i] <= 2);
        if (heladas.length) al.push({ ic:"❄️", nivel:"danger", title:"Riesgo de heladas",
          periodo: heladas.map(fday).join(", "), fuente:"Open-Meteo",
          desc:`Mínimas de hasta ${Math.round(Math.min(...heladas.map(i=>dd.temperature_2m_min[i])))}°C previstas en tus potreros. Protegé praderas tiernas y revisá el agua de bebederos.` });
        const lluviaTotal = dd.precipitation_sum.reduce((a,b)=>a+(b||0),0);
        if (lluviaTotal >= 40) al.push({ ic:"🌧", nivel:"warn", title:"Lluvias importantes",
          periodo:"7 días", fuente:"Open-Meteo",
          desc:`Se esperan ~${Math.round(lluviaTotal)} mm en la semana sobre tus potreros. Cuidá zonas bajas y el pisoteo en suelos húmedos.` });
        const calor = dd.time.map((_,i)=>i).filter(i=>dd.temperature_2m_max[i] >= 34);
        if (calor.length) al.push({ ic:"🥵", nivel:"warn", title:"Calor extremo",
          periodo: calor.map(fday).join(", "), fuente:"Open-Meteo",
          desc:`Máximas ≥34°C. Asegurá sombra y agua suficiente para el rodeo.` });
        setAlertasClima(al);
      }).catch(()=>setAlertasClima([]));
  }, [centroPotreros?.[0], centroPotreros?.[1]]);

  // Helpers mapa (Leaflet)
  const handleMapPoint  = useCallback((latLng) => setCurrentPts(prev=>[...prev, latLng]), []);
  const handleMapFinish = useCallback((cleanPts) => {
    setPendingPts(cleanPts);
    setDrawing(false);
    setCurrentPts([]);
  }, []);
  const handleClosePotrero = () => {
    if (currentPts.length >= 3) {
      setPendingPts([...currentPts]);
      setDrawing(false);
      setCurrentPts([]);
    }
  };

  const handleSaveNewPot = async ({ nombre, pasto, estado, haReal, obs, tipo }) => {
    const newPotrero = {
      nombre,
      pasto,
      estado,
      color: ESTADO_COLOR[estado],
      ha: haReal || calcAreaHaGeo(pendingPts),
      points: pendingPts,
      obs,
      tipo: tipo || "cultivado",
    };

    try {
      // Guardar en tabla potreros (usando hook)
      await potreroState.createPotrero(newPotrero);
      setPendingPts(null);
    } catch (e) {
      console.error("Error guardando potrero:", e);
      toast.error("No se pudo guardar el potrero: " + (e.message || "error"));
    }
  };

  const updatePotrero = async (updated) => {
    try {
      // Remover JSON anidados antes de actualizar en BD
      const { actividades, animalesAsignados, ...potreroData } = updated;
      await potreroState.updatePotrero(updated.id, potreroData);
      // Actualizar estado local con el cambio
      setPotreros(prev=>prev.map(p=>p.id===updated.id ? updated : p));
    } catch (e) {
      console.error("Error actualizando potrero:", e);
      toast.error("No se pudo actualizar el potrero: " + (e.message || "error"));
    }
  };

  // Mueve un vértice del potrero en edición y recalcula las hectáreas
  const moverVertice = (potreroId, idx, latLng) =>
    setPotreros(prev=>prev.map(p=>{
      if (p.id!==potreroId) return p;
      const points = p.points.map((pt,i)=> i===idx ? latLng : pt);
      return { ...p, points, ha: calcAreaHaGeo(points) };
    }));

  // Elimina un vértice del potrero en edición (mínimo 3) y recalcula hectáreas
  const eliminarVertice = (potreroId, idx) =>
    setPotreros(prev=>prev.map(p=>{
      if (p.id!==potreroId) return p;
      if ((p.points||[]).length<=3) return p; // un polígono necesita al menos 3
      const points = p.points.filter((_,i)=>i!==idx);
      return { ...p, points, ha: calcAreaHaGeo(points) };
    }));

  // Inserta un vértice en una arista del potrero en edición y recalcula hectáreas
  const insertarVertice = (potreroId, idx, latLng) =>
    setPotreros(prev=>prev.map(p=>{
      if (p.id!==potreroId) return p;
      const points = [...p.points];
      points.splice(idx, 0, latLng);
      return { ...p, points, ha: calcAreaHaGeo(points) };
    }));

  // Descuenta una ración específica del stock de Insumos y la registra en el historial
  const consumirRacion = async (insumoId, kg, zonaNombre, fecha) => {
    if (!insumoId || !(kg > 0)) return;
    try {
      const it = insumos.find(i => i.id === insumoId);
      if (!it) return;
      const usado = Math.min(it.cantidad || 0, kg);
      await insumosState.updateInsumo(insumoId, { cantidad: Math.max(0, (it.cantidad || 0) - kg) });
      if (user?.id) {
        await movimientoInsumoService.create(user.id, insumoId, {
          tipo: "consumo", cantidad: usado,
          fecha: fecha || new Date().toISOString().slice(0, 10),
          concepto: `Ración · ${zonaNombre || "zona"}`,
        });
      }
    } catch (e) {
      console.error("Error consumiendo ración:", e);
      toast.error("No se pudo descontar la ración del stock.");
    }
  };

  // Quita un punto mientras se dibuja un potrero nuevo
  const removeDrawPoint = (idx) => setCurrentPts(prev=>prev.filter((_,i)=>i!==idx));

  const deletePotrero = async (potreroId) => {
    try {
      await potreroState.deletePotrero(potreroId);
      setSelected(null);
      setShowFicha(false);
    } catch (e) {
      console.error("Error eliminando potrero:", e);
      toast.error("No se pudo eliminar el potrero: " + (e.message || "error"));
    }
  };

  // Helpers rodeo - refactorizados para usar servicios
  const updateAnimal = async (potreroId, updated) => {
    try {
      // Remover JSON anidados antes de actualizar en BD (normalizar a snake_case)
      const { pesadas, eventos, movimientos, sanidad, ...animalData } = updated;
      await animalService.update(updated.id, normalizeAnimal(animalData));

      // Guardar pesadas en BD (nuevas vs antiguas)
      if (pesadas && pesadas.length > 0 && user?.id) {
        const viejasPesadas = (potreros.find(p=>p.id===potreroId)?.animalesAsignados||[]).find(a=>a.id===updated.id)?.pesadas || [];
        const nuevasPesadas = pesadas.filter(p => !viejasPesadas.find(v => v.id === p.id && v.fecha === p.fecha && v.kg === p.kg));
        for (const p of nuevasPesadas) {
          await pesadaService.create(user.id, updated.id, { fecha: p.fecha, kg: p.kg, obs: p.obs });
        }
      }

      // Guardar eventos en BD (nuevos vs antiguos)
      if (eventos && eventos.length > 0 && user?.id) {
        const viejosEventos = (potreros.find(p=>p.id===potreroId)?.animalesAsignados||[]).find(a=>a.id===updated.id)?.eventos || [];
        const nuevosEventos = eventos.filter(e => !viejosEventos.find(v => v.id === e.id && v.fecha === e.fecha && v.tipo === e.tipo));
        for (const e of nuevosEventos) {
          await eventoReproduccionService.create(user.id, updated.id, { tipo: e.tipo, fecha: e.fecha, obs: e.obs });
        }
      }

      setPotreros(prev=>prev.map(p=>
        p.id===potreroId ? {...p, animalesAsignados:p.animalesAsignados.map(a=>a.id===updated.id?updated:a)} : p
      ));
    } catch (e) {
      console.error("Error actualizando animal:", e);
      toast.error("No se pudo actualizar el animal: " + (e.message || "error"));
    }
  };

  const moverAnimal = async (animalId, fromId, toId, mov) => {
    try {
      // Registrar movimiento en tabla. El origen/destino viajan como argumentos
      // (fromId/toId), no dentro de `mov`: sin esto la fila queda con
      // potrero_desde_id/potrero_hasta_id en null y el historial pierde el "de → a".
      await movimientoService.create(user?.id, animalId, { ...mov, desdeId: fromId, hastaId: toId });

      // Actualizar animal (cambiar potrero_id)
      await animalService.update(animalId, { potrero_id: toId });

      // Actualizar estado local
      setPotreros(prev=>{
        const animal = prev.find(p=>p.id===fromId)?.animalesAsignados?.find(a=>a.id===animalId);
        if (!animal) return prev;
        // El animal en memoria es camelCase (ver hidratarAnimal): actualizamos
        // potreroId, no potrero_id, para no dejar el campo viejo desincronizado.
        const upd = { ...animal, potreroId: toId };
        return prev.map(p=>{
          if (p.id===fromId) return {...p, animalesAsignados:p.animalesAsignados.filter(a=>a.id!==animalId)};
          if (p.id===toId)   return {...p, animalesAsignados:[...(p.animalesAsignados||[]),upd]};
          return p;
        });
      });
    } catch (e) {
      console.error("Error moviendo animal:", e);
      toast.error("No se pudo mover el animal: " + (e.message || "error"));
    }
  };

  const eliminarAnimal = async (animalId, potreroId) => {
    try {
      await animalService.delete(animalId);
      setPotreros(prev=>prev.map(p=>
        p.id===potreroId ? {...p, animalesAsignados:p.animalesAsignados.filter(a=>a.id!==animalId)} : p
      ));
    } catch (e) {
      console.error("Error eliminando animal:", e);
      toast.error("No se pudo eliminar el animal: " + (e.message || "error"));
    }
  };

  const agregarAnimal = async (potreroId, animal) => {
    try {
      const usuarioId = user?.id;
      const campoId = profile?.campo_propietario_id || user?.id;

      // Separar campos normalizados (tabla animales) de los JSON anidados (tablas separadas)
      const { pesadas, eventos, movimientos, sanidad, ...animalData } = animal;

      // Normalizar a snake_case y limpiar valores vacíos
      const normalizedData = normalizeAnimal({
        ...animalData,
        potrero_id: potreroId,
      });

      // Guardar solo columnas de tabla animales
      const saved = await animalService.create(usuarioId, campoId, normalizedData);

      // Guardar peso inicial si existe (buscar tanto en camelCase como snake_case)
      const pesoInicial = animal.pesoInicial || animal.peso_inicial || 0;
      if (pesoInicial > 0) {
        const hoy = new Date().toISOString().slice(0, 10);
        await pesadaService.create(usuarioId, saved.id, {
          fecha: hoy,
          kg: pesoInicial,
        });
      }

      // Actualizar estado local con estructura completa (convertir snake_case a camelCase)
      const nuevoAnimal = {
        ...denormalizeData(saved),
        pesadas: animal.pesadas || [],
        eventos: animal.eventos || [],
        movimientos: animal.movimientos || [],
        sanidad: animal.sanidad || [],
      };

      setPotreros(prev=>prev.map(p=>
        p.id===potreroId ? {...p, animalesAsignados:[...(p.animalesAsignados||[]),nuevoAnimal]} : p
      ));
    } catch (e) {
      console.error("Error agregando animal:", e);
      toast.error("No se pudo agregar el animal: " + (e.message || "error"));
    }
  };

  // Helpers sanidad - refactorizado para usar servicios
  const aplicarTratamiento = async (trat, sel) => {
    try {
      const campoId = profile?.campo_propietario_id || user?.id;
      // Registrar tratamiento para cada animal
      for (const s of sel) {
        await tratamientoService.create(user?.id, campoId, s.animalId, trat);
      }

      // Actualizar estado local
      setPotreros(prev=>prev.map(p=>{
        const ids = sel.filter(s=>s.potreroId===p.id).map(s=>s.animalId);
        if (!ids.length) return p;
        return { ...p, animalesAsignados:p.animalesAsignados.map(a=>
          ids.includes(a.id) ? {...a, sanidad:[...(a.sanidad||[]),trat]} : a
        )};
      }));

      // Descontar del stock de sanidad (dosis × cantidad de animales)
      const total = trat.total || ((parseFloat(trat.dosis)||0) * sel.length);
      if (trat.productoId && total>0) {
        const stock = stockSanidad.find(s => s.id === trat.productoId);
        if (stock) {
          await sanidadState.updateStock(trat.productoId, {
            cantidad: Math.max(0, (stock.cantidad||0) - total)
          });
        }
      }
    } catch (e) {
      console.error("Error aplicando tratamiento:", e);
      toast.error("No se pudo aplicar el tratamiento: " + (e.message || "error"));
    }
  };

  const addTransaccion = async (t) => {
    try {
      const transaccion = { ...t, ts: Date.now(), hora: new Date().toTimeString().slice(0,5) };
      await economiaState.createTransaccion(transaccion);
    } catch (e) {
      console.error("Error creando transacción:", e);
      toast.error("No se pudo guardar el movimiento económico.");
    }
  };

  const addFardo = async (f) => {
    try {
      await fardosState.createFardo(f);
    } catch (e) {
      console.error("Error creando fardo:", e);
    }
  };

  const updateFardo = async (f) => {
    try {
      // Remover JSON anidados antes de actualizar en BD
      const { animales_distribuidos, distribuciones, ...fardoData } = f;
      await fardosState.updateFardo(f.id, fardoData);
    } catch (e) {
      console.error("Error actualizando fardo:", e);
    }
  };

  // Al enfardar en un potrero: suma a un fardo existente que coincida (pasto+tipo) o crea uno nuevo
  const enfardarDesdeActividad = async ({ pasto, tipoFardo, cantidad, potreroId, fecha }) => {
    if (!cantidad || cantidad <= 0) return;
    try {
      const fardoExistente = fardos.find(f => f.pasto_principal===pasto && f.tipo===tipoFardo && f.estado==="disponible");

      if (fardoExistente) {
        // Actualizar fardo existente
        await fardosState.updateFardo(fardoExistente.id, {
          cantidad: (fardoExistente.cantidad||0) + cantidad
        });
      } else {
        // Crear nuevo fardo
        await fardosState.createFardo({
          pasto_principal: pasto || "—",
          tipo: tipoFardo || "Redondo",
          cantidad,
          peso_promedio: 0,
          potrero_id: potreroId,
          fecha: fecha || new Date().toISOString().slice(0,10),
          packer: "",
          estado: "disponible",
          obs: "Generado al enfardar"
        });
      }
    } catch (e) {
      console.error("Error enfardar desde actividad:", e);
    }
  };

  // Descuenta insumos consumidos por una actividad y registra el movimiento (dónde se usó)
  const consumirInsumos = async ({ litros=0, semillaNombre, semillaKg=0, potreroNombre, actividadTipo, fecha }) => {
    const f = fecha || new Date().toISOString().slice(0,10);
    const detalle = `${actividadTipo||"Actividad"} · ${potreroNombre||"potrero"}`;
    const restar = async (it, cant) => {
      if (!it || !(cant > 0)) return;
      const usado = Math.min(it.cantidad||0, cant);
      try {
        await insumosState.updateInsumo(it.id, { cantidad: Math.max(0,(it.cantidad||0)-cant) });
        if (user?.id) {
          await movimientoInsumoService.create(user.id, it.id, {
            tipo:"consumo", cantidad:usado, fecha:f, concepto:detalle,
          });
        }
      } catch (e) { console.error("Error consumiendo insumo:", e); }
    };
    if (litros>0) {
      await restar(insumos.find(x=>x.categoria==="Gasoil"), litros);
    }
    if (semillaKg>0 && semillaNombre) {
      await restar(insumos.find(x=>x.categoria==="Semilla" && x.nombre===semillaNombre), semillaKg);
    }
  };
  const deleteFardo = async (id) => {
    try {
      await fardosState.deleteFardo(id);
    } catch (e) {
      console.error("Error eliminando fardo:", e);
    }
  };

  // Venta de animal desde RodeoPanel → registra ingreso en Economía
  const venderAnimal = async (potreroId, animal, ventaData) => {
    try {
      const potNombre = potreros.find(p=>p.id===potreroId)?.nombre || "";

      // Marcar animal como vendido: guarda datos de venta + saca del potrero
      await animalService.update(animal.id, {
        estado: "vendido",
        deleted_at: new Date().toISOString(),
        fecha_venta: ventaData.fecha || null,
        precio_venta: ventaData.total || 0,
        comprador: ventaData.comprador || null,
        peso_venta: ventaData.pesoVenta || null,
        potrero_origen: potNombre,
      });

      // Actualizar estado local
      setPotreros(prev=>prev.map(p=>
        p.id===potreroId ? { ...p, animalesAsignados:(p.animalesAsignados||[]).filter(a=>a.id!==animal.id) } : p));
      setVendidos(prev=>[...prev, {
        ...animal, estado:"vendido", potreroOrigen:potNombre, fechaVenta:ventaData.fecha,
        eventos:[...(animal.eventos||[]), { id:uid(), tipo:"Venta", fecha:ventaData.fecha, datos:ventaData, obs:"" }],
      }]);

      // Registrar ingreso
      await addTransaccion({
        tipo:"ingreso", categoria:"Venta de animales",
        concepto:`Venta #${animal.caravana||"—"} ${animal.categoria}${ventaData.comprador?" → "+ventaData.comprador:""}`,
        fecha: ventaData.fecha, monto: ventaData.total || 0,
        obs:`${ventaData.pesoVenta} kg × $${ventaData.precioKg}/kg`,
      });
      toast.success(`Venta registrada · $${(ventaData.total||0).toLocaleString("es-UY")}`);
    } catch (e) {
      console.error("Error vendiendo animal:", e);
      toast.error("No se pudo registrar la venta: " + (e.message || "error"));
    }
  };

  // Revertir una venta: el animal vuelve al rodeo, a su potrero original.
  // No pasa por updateAnimal() a propósito: normalizeAnimal() descarta los null,
  // así que los campos de venta y el deleted_at nunca se limpiarían y el animal
  // quedaría fuera del rodeo para siempre (getByPotrero filtra deleted_at null).
  const revertirVenta = async (animal) => {
    try {
      await animalService.update(animal.id, {
        estado: "activo",
        deleted_at: null,
        fecha_venta: null,
        precio_venta: null,
        comprador: null,
        peso_venta: null,
        potrero_origen: null,
      });

      // Sacarlo de vendidos y devolverlo a su potrero (venderAnimal nunca tocó
      // potrero_id, así que sigue apuntando al potrero de origen).
      setVendidos(prev => prev.filter(a => a.id !== animal.id));
      const vuelto = { ...animal, estado:"activo", fechaVenta:null, precioVenta:null,
        comprador:null, pesoVenta:null, potreroOrigen:null };
      setPotreros(prev => prev.map(p =>
        p.id === (animal.potreroId || vuelto.potreroId)
          ? { ...p, animalesAsignados:[...(p.animalesAsignados||[]), vuelto] }
          : p));
      toast.success(`Venta revertida · #${animal.caravana||"—"} vuelve al rodeo`);
    } catch (e) {
      console.error("Error revirtiendo venta:", e);
      toast.error("No se pudo revertir la venta: " + (e.message || "error"));
    }
  };

  const potreroSel = potreros.find(p=>p.id===selected);
  const totalHa    = potreros.reduce((s,p)=>s+p.ha, 0);
  const totalAnim  = potreros.reduce((s,p)=>s+(p.animalesAsignados||[]).length, 0);
  // Todos los animales de todos los potreros (para Sanidad, Rodeo, etc.)
  const todosLosAnimales = potreros.flatMap(p => p.animalesAsignados || []);
  const totalUGglobal = todosLosAnimales.reduce((s,a)=>s+ugAnimal(a), 0);
  const cargaGlobalHome = totalHa ? (totalUGglobal/totalHa) : 0;
  const totalGasoil  = insumos.filter(i=>i.categoria==="Gasoil").reduce((s,i)=>s+(i.cantidad||0),0);
  const totalSemilla = insumos.filter(i=>i.categoria==="Semilla").reduce((s,i)=>s+(i.cantidad||0),0);

  // Alertas derivadas de la Agenda (tareas vencidas / de esta semana)
  const duTarea = (f) => -diasDesde(f); // días hasta la fecha (negativo = vencida)
  const tPend = tareas.filter(t=>t.estado!=="hecha");
  const tVenc = tPend.filter(t=>duTarea(t.fecha) < 0).sort((a,b)=>a.fecha.localeCompare(b.fecha));
  const tSem  = tPend.filter(t=>{ const d=duTarea(t.fecha); return d>=0 && d<=7; }).sort((a,b)=>a.fecha.localeCompare(b.fecha));
  const tProx = tPend.filter(t=>duTarea(t.fecha) > 7).sort((a,b)=>a.fecha.localeCompare(b.fecha));

  // Alertas de stock bajo (insumos + sanidad)
  const hoyISO = new Date().toISOString().slice(0,10);
  const stockAlertas = [
    ...insumos.filter(i=>i.stockMin>0 && (i.cantidad||0)<i.stockMin)
      .map(i=>({ id:"i"+i.id, nombre:i.nombre, det:`${i.cantidad} ${i.unidad} · mín ${i.stockMin}`, nav:"insumos" })),
    ...stockSanidad.filter(s=>(s.stockMin>0 && (s.cantidad||0)<s.stockMin) || (s.vencimiento && s.vencimiento<hoyISO))
      .map(s=>({ id:"s"+s.id, nombre:s.nombre,
        det:(s.vencimiento && s.vencimiento<hoyISO) ? "⏰ vencido" : `${s.cantidad} ${s.unidad} · mín ${s.stockMin}`, nav:"sanidad" })),
  ];

  // ── Navegación (sidebar desktop + bottom-nav mobile) ──
  const isSuperAdmin = profile?.rol === "superadmin";
  const navVisible = (id) => id === "inicio" || id === "mapa" || (id === "admin" ? isSuperAdmin : isFeatureOn(features, id));
  const tareasPend = tareas.filter(t => t.estado !== "hecha").length;

  useEffect(() => {
    if (!navVisible(activeNav)) setActiveNav("inicio");
  }, [features, isSuperAdmin]); // eslint-disable-line

  // Atajo "/" para abrir la búsqueda global
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !["INPUT","TEXTAREA","SELECT"].includes(document.activeElement?.tagName)) {
        e.preventDefault(); setShowBuscar(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Selector de campo (estilo claro)
  const campoChip = editNombre ? (
    <input value={nombreCampo} onChange={e=>setNombreCampo(e.target.value)} autoFocus
      onBlur={()=>setEditNombre(false)} onKeyDown={e=>e.key==="Enter"&&setEditNombre(false)}
      style={{ background:C.arena, border:`1px solid ${C.arenaDark}`, color:C.tierra, borderRadius:9,
        padding:"7px 10px", fontSize:13, width:"100%", fontFamily:"sans-serif", boxSizing:"border-box" }}/>
  ) : camposDisponibles && camposDisponibles.length > 0 ? (
    <select value={campoActivo?.id || ""} onChange={(e) => {
      if (e.target.value === "nuevo") setMostrarCrearCampo(true);
      else { const campo = camposDisponibles.find(c => c.id === e.target.value); if (campo && onCampoChange) onCampoChange(campo); }
    }} style={{ background:C.arena, border:`1px solid ${C.arenaDark}`, color:C.tierra, borderRadius:9,
      padding:"7px 10px", fontSize:13, width:"100%", fontFamily:"sans-serif", cursor:"pointer", boxSizing:"border-box" }}>
      {camposDisponibles.map(c => <option key={c.id} value={c.id}>🏡 {c.nombre}</option>)}
      <option value="nuevo">➕ Crear nuevo campo</option>
    </select>
  ) : (
    <span onClick={()=>setEditNombre(true)} title="Editar nombre del campo"
      style={{ fontSize:13, color:C.tierra, cursor:"pointer", fontWeight:600, padding:"7px 10px", borderRadius:9,
        background:C.arena, border:`1px solid ${C.arenaDark}`, display:"inline-flex", alignItems:"center", gap:6,
        whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
      🏡 {nombreCampo}{ubicacion && <span style={{ opacity:0.6, fontSize:11 }}>· 📍 {ubicacion}</span>}<span style={{ opacity:0.4, fontSize:11 }}>✎</span>
    </span>
  );

  // Controles del topbar (búsqueda + IA + Mis datos), estilo claro
  const topControls = (
    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
      <button onClick={()=>setShowBuscar(true)} title="Buscar (/)"
        style={{ background:C.white, color:C.muted, border:`1px solid ${C.arenaDark}`, borderRadius:9,
          width:36, height:36, cursor:"pointer", fontSize:15 }}>🔎</button>
      <button onClick={()=>setShowIA(true)} disabled={potreros.length===0} title="Asesor IA"
        style={{ background:C.pasto, color:"#fff", border:"none", borderRadius:9, padding:"7px 12px",
          fontSize:13, fontWeight:600, cursor: potreros.length? "pointer":"not-allowed", opacity: potreros.length?1:0.5, whiteSpace:"nowrap" }}>
        ✦ {isMobile ? "IA" : "Asesor IA"}
      </button>
      {!isMobile && (
        <button onClick={()=>setShowMisDatos(true)} title="Mis datos"
          style={{ background:C.white, color:C.muted, border:`1px solid ${C.arenaDark}`, borderRadius:9,
            width:36, height:36, cursor:"pointer", fontSize:15 }}>⚙</button>
      )}
    </div>
  );

  // Botón de nav (usado en sidebar y en el sheet "Más")
  const NavBtn = ({ id, inSidebar }) => {
    const n = NAV_MAP[id]; const on = activeNav === id;
    return (
      <button onClick={()=>{ setActiveNav(id); setMostrarMas(false); }} title={inSidebar && sidebarCollapsed ? n.l : ""}
        style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:inSidebar&&sidebarCollapsed?"center":"flex-start", gap:11,
        padding:"9px 12px", borderRadius:9, border:"none", cursor:"pointer", textAlign:"left", fontSize:14, fontFamily:"sans-serif",
        background: on?C.arena:"transparent", color: on?C.tierra:C.muted, fontWeight: on?600:500 }}>
        <span style={{ width:20, textAlign:"center", fontSize:15 }}>{n.ic}</span>
        {!(inSidebar && sidebarCollapsed) && n.l}
        {!(inSidebar && sidebarCollapsed) && id==="agenda" && tareasPend>0 && <span style={{ marginLeft:"auto", background:C.danger, color:"#fff", fontSize:10, fontWeight:700, borderRadius:20, padding:"1px 7px", fontFamily:"monospace" }}>{tareasPend}</span>}
      </button>
    );
  };

  // Sidebar (desktop)
  const sidebar = (
    <aside style={{ width:sidebarCollapsed?60:232, background:C.white, borderRight:`1px solid ${C.arenaDark}`, display:"flex",
      flexDirection:"column", flexShrink:0, height:"100vh", overflowY:"auto", transition:"width 0.3s ease", position:"relative" }}>
      <div style={{ padding:"16px 18px 10px", display:"flex", alignItems:"center", justifyContent:sidebarCollapsed?"center":"flex-start", gap:10, position:"relative" }}>
        <button onClick={toggleSidebar} style={{ position:"absolute", top:4, right:4, background:"none", border:"none", cursor:"pointer", fontSize:16, padding:4, opacity:0.7, transition:"opacity 0.2s" }} title={sidebarCollapsed?"Expandir":"Contraer"}>
          {sidebarCollapsed?"→":"←"}
        </button>
        <div style={{ width:30, height:30, borderRadius:9, background:C.pasto, display:"grid", placeItems:"center", fontSize:16 }}>🌾</div>
        {!sidebarCollapsed && <div style={{ fontFamily:"serif", fontSize:19, fontWeight:600, color:C.tierra }}>Campo<span style={{color:C.pasto,fontStyle:"italic"}}>IA</span></div>}
      </div>
      <div style={{ padding:sidebarCollapsed?"2px 8px":"0 12px 8px", maxWidth:sidebarCollapsed?"100%":"100%" }}>{campoChip}</div>
      <nav style={{ padding:"2px 10px", flex:1 }}>
        {NAV_GROUPS.map((g,gi)=>{
          const ids = g.ids.filter(navVisible);
          if (!ids.length) return null;
          return (
            <div key={gi} style={{ marginBottom:4 }}>
              {g.label && !sidebarCollapsed && <div style={{ fontFamily:"monospace", fontSize:9.5, textTransform:"uppercase", letterSpacing:".09em", color:C.mutedLight, padding:"8px 12px 3px", fontWeight:600 }}>{g.label}</div>}
              {ids.map(id => <NavBtn key={id} id={id} inSidebar={true} />)}
            </div>
          );
        })}
      </nav>
      <div style={{ padding:"10px 14px", borderTop:`1px solid ${C.arenaDark}`, display:"flex", alignItems:"center", justifyContent:sidebarCollapsed?"center":"flex-start", gap:10 }}>
        <div style={{ width:28, height:28, borderRadius:"50%", background:C.descanso, color:"#2a1c07", display:"grid", placeItems:"center", fontWeight:700, fontSize:12, flexShrink:0, cursor:"pointer" }} onClick={()=>setShowMisDatos(true)} title="Mis datos">
          {(user?.email||"U")[0].toUpperCase()}
        </div>
        {!sidebarCollapsed && <div style={{ flex:1, minWidth:0 }}>{LogoutButton && <LogoutButton />}</div>}
      </div>
    </aside>
  );

  // Bottom-nav (mobile)
  const BOTTOM_IDS = ["inicio","rodeo","agenda","economia"];
  const bottomNav = (
    <nav style={{ display:"flex", flexShrink:0, background:C.white, borderTop:`1px solid ${C.arenaDark}`, padding:"5px 4px" }}>
      {BOTTOM_IDS.map(id=>{ const n=NAV_MAP[id]; const on=activeNav===id; return (
        <button key={id} onClick={()=>setActiveNav(id)} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2,
          background:"none", border:"none", cursor:"pointer", padding:"5px 0", position:"relative",
          color: on?C.pasto:C.mutedLight, fontWeight: on?700:500 }}>
          <span style={{ fontSize:19 }}>{n.ic}</span><span style={{ fontSize:10.5 }}>{n.l}</span>
          {id==="agenda" && tareasPend>0 && <span style={{ position:"absolute", top:2, right:"calc(50% - 20px)", background:C.danger, color:"#fff", fontSize:9, borderRadius:20, padding:"0 5px", fontFamily:"monospace", fontWeight:700 }}>{tareasPend}</span>}
        </button>
      );})}
      <button onClick={()=>setMostrarMas(true)} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2,
        background:"none", border:"none", cursor:"pointer", padding:"5px 0", color:C.mutedLight, fontWeight:500 }}>
        <span style={{ fontSize:19 }}>☰</span><span style={{ fontSize:10.5 }}>Más</span>
      </button>
    </nav>
  );

  // Topbar
  const topbar = (
    <header style={{ display:"flex", alignItems:"center", gap:12, padding: isMobile?"8px 12px":"9px 20px",
      background:C.white, borderBottom:`1px solid ${C.arenaDark}`, flexShrink:0, zIndex:10 }}>
      {isMobile ? (
        <>
          <div style={{ fontFamily:"serif", fontSize:17, fontWeight:600, color:C.tierra, flexShrink:0 }}>Campo<span style={{color:C.pasto,fontStyle:"italic"}}>IA</span></div>
          <div style={{ flex:1, minWidth:0 }}>{campoChip}</div>
        </>
      ) : (
        <div style={{ fontFamily:"serif", fontSize:18, fontWeight:600, color:C.tierra }}>
          {NAV_MAP[activeNav]?.ic} {NAV_MAP[activeNav]?.l}
        </div>
      )}
      <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:12 }}>
        {!isMobile && clima?.[0] && <span style={{ fontSize:13, color:C.muted, whiteSpace:"nowrap" }}>{clima[0].ic} {clima[0].max}°/{clima[0].min}°</span>}
        {!isMobile && (
          <span style={{ fontSize:11, fontFamily:"monospace", whiteSpace:"nowrap", color: syncStatus==="error"?C.danger:C.mutedLight }}>
            {syncStatus==="saving" && "⏳ Guardando…"}{syncStatus==="saved" && "☁️ Guardado"}{syncStatus==="error" && "⚠️ Error"}
          </span>
        )}
        {topControls}
      </div>
    </header>
  );

  // Sheet "Más" (mobile): todas las secciones
  const moreSheet = mostrarMas && (
    <div onClick={()=>setMostrarMas(false)} style={{ position:"fixed", inset:0, background:"rgba(31,18,10,0.4)", zIndex:2000, display:"flex", alignItems:"flex-end" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:C.white, width:"100%", maxHeight:"80vh", overflowY:"auto",
        borderRadius:"16px 16px 0 0", padding:"10px 12px 24px" }}>
        <div style={{ width:38, height:4, borderRadius:4, background:C.arenaDark, margin:"4px auto 10px" }}/>
        <div style={{ padding:"0 4px 6px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ fontFamily:"serif", fontSize:17, fontWeight:600, color:C.tierra }}>Todas las secciones</div>
          <button onClick={()=>setShowMisDatos(true)} style={{ background:C.arena, border:`1px solid ${C.arenaDark}`, borderRadius:8, padding:"6px 10px", fontSize:12, cursor:"pointer", color:C.tierra }}>⚙ Mis datos</button>
        </div>
        {NAV_GROUPS.map((g,gi)=>{ const ids=g.ids.filter(navVisible); if(!ids.length) return null; return (
          <div key={gi} style={{ marginBottom:4 }}>
            {g.label && <div style={{ fontFamily:"monospace", fontSize:9.5, textTransform:"uppercase", letterSpacing:".09em", color:C.mutedLight, padding:"8px 12px 3px", fontWeight:600 }}>{g.label}</div>}
            {ids.map(id => <NavBtn key={id} id={id} />)}
          </div>
        );})}
        <div style={{ padding:"10px 12px 0", borderTop:`1px solid ${C.arenaDark}`, marginTop:6 }}>{LogoutButton && <LogoutButton />}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display:"flex", height:"100vh", background:C.fondo, overflow:"hidden", fontFamily:"sans-serif" }}>
      {!isMobile && sidebar}
      <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0, height:"100vh" }}>
        {topbar}
        {activeNav!=="inicio" && (
          <div style={{ display:"flex", alignItems:"center",
        background:C.white, borderBottom:`1px solid ${C.arenaDark}`, padding: isMobile?"7px 10px":"8px 16px",
        flexShrink:0, overflowX:"auto", zIndex:1000, gap:0 }}>

        {/* Clima de tus potreros, contra la izquierda (con loading) */}
        {climaPotreros ? (
          <div style={{ display:"flex", gap:7, alignItems:"center", flexShrink:0,
            paddingRight:16, marginRight:8, borderRight:`1px solid ${C.arenaDark}` }}>
            {climaPotreros.slice(0,7).map((d,i)=>(
              <div key={i} title={`${d.dia}: ${d.max}°/${d.min}° · 💧${d.prob}%${d.lluvia>0?` · ${d.lluvia}mm`:""}`}
                style={{ display:"flex", flexDirection:"column", alignItems:"center", lineHeight:1.2, minWidth:36 }}>
                <span style={{ fontSize:10.5, color:C.muted, fontFamily:"monospace", fontWeight:600 }}>{d.dia}</span>
                <span style={{ fontSize:21 }}>{d.ic}</span>
                <span style={{ fontSize:13, fontWeight:700, color:C.tierra, fontFamily:"monospace" }}>{d.max}°</span>
                <span style={{ fontSize:10, fontFamily:"monospace", color: d.prob>=50?"#2A6FB0":C.muted, fontWeight:d.prob>=50?700:500 }}>💧{d.prob}%</span>
              </div>
            ))}
          </div>
        ) : potreros.length>0 ? (
          <div style={{ display:"flex", gap:9, alignItems:"center", flexShrink:0,
            paddingRight:16, marginRight:8, borderRight:`1px solid ${C.arenaDark}`, color:C.muted, fontSize:12 }}>
            <span style={{ display:"inline-block", width:16, height:16, borderRadius:"50%",
              border:`2px solid ${C.arenaDark}`, borderTopColor:C.pasto, animation:"campoia-spin 0.8s linear infinite" }}/>
            <span style={{ animation:"campoia-pulse 1.2s ease-in-out infinite" }}>🌦️ Cargando clima…</span>
          </div>
        ) : null}

        {/* Stats centrados en el espacio restante */}
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", overflowX:"auto", minWidth:0 }}>
          <StatInline ic="🌾" val={totalHa.toFixed(0)} label="hectáreas"/>
          <StatInline ic="🐂" val={totalAnim} label="animales"/>
          <StatInline ic="🗺" val={potreros.length} label="potreros"/>
          <StatInline ic="⛽" val={`${totalGasoil.toLocaleString("es-UY")}`} label="L gasoil"/>
          <StatInline ic="🌱" val={`${totalSemilla.toLocaleString("es-UY")}`} label="kg semilla"/>
          <StatInline ic="📋" val={potreros.reduce((s,p)=>s+(p.actividades||[]).length,0)} label="actividades" last/>
        </div>

        {/* Indicador de sincronización (derecha) */}
        <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, fontFamily:"monospace",
          whiteSpace:"nowrap", flexShrink:0, paddingLeft:14, color: syncStatus==="error" ? C.danger : C.muted }}>
          {syncStatus==="saving" && <>⏳ Guardando…</>}
          {syncStatus==="saved"  && <span style={{ color:C.pasto }}>☁️ Guardado</span>}
          {syncStatus==="error"  && <>⚠️ Error al guardar</>}
        </div>
      </div>
        )}

        {/* ── CONTENIDO ── */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", minHeight:0 }}>
      {activeNav==="inicio" && (
        <HomePanel
          nombreCampo={nombreCampo}
          ubicacion={ubicacion}
          potreros={potreros}
          transacciones={transacciones}
          tareas={tareas}
          stockSanidad={stockSanidad}
          insumos={insumos}
          pesadas={pesadasCampo}
          totalHa={totalHa}
          cargaGlobal={cargaGlobalHome}
          clima={clima}
          onNav={(nav, aviso) => {
            if (aviso) setAvisoParaAgenda(aviso);
            setActiveNav(nav);
          }}
        />
      )}
      {activeNav==="mapa" && (
        <div style={{ display:"flex", flexDirection: isMobile?"column":"row", flex:1, overflow:"hidden", position:"relative" }}>

          {/* Mobile: barra para abrir el panel */}
          {isMobile && (
            <div style={{ flexShrink:0, background:C.white, borderBottom:`1px solid ${C.arenaDark}`, padding:"8px 12px" }}>
              <Btn size="sm" variant="secondary" onClick={()=>setShowSidebar(true)} style={{ width:"100%" }}>
                ☰ Ver panel · potreros, precios, clima
              </Btn>
            </div>
          )}

          {/* Backdrop del drawer en mobile */}
          {isMobile && showSidebar && (
            <div onClick={()=>setShowSidebar(false)}
              style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.35)", zIndex:1400 }}/>
          )}

          {/* Sidebar mapa (drawer deslizable en mobile) */}
          <aside style={{
            width: isMobile ? "84%" : sidebarWidth, maxWidth: isMobile ? 330 : "none",
            background:C.white, borderRight:`1px solid ${C.arenaDark}`,
            overflowY:"auto", flexShrink:0, zIndex: isMobile?1500:500,
            ...(isMobile ? {
              position:"absolute", top:0, bottom:0, left:0,
              transform: showSidebar ? "translateX(0)" : "translateX(-100%)",
              transition:"transform 0.25s ease",
              boxShadow: showSidebar ? "0 0 24px rgba(0,0,0,0.3)" : "none",
            } : {}),
          }}>
            {isMobile && (
              <div style={{ display:"flex", justifyContent:"flex-end", padding:"8px 12px 0" }}>
                <button onClick={()=>setShowSidebar(false)}
                  style={{ background:"none", border:"none", fontSize:24, lineHeight:1, color:C.muted, cursor:"pointer" }}>×</button>
              </div>
            )}

            {/* Precios de hacienda (siempre visible) */}
            <Section label="Precios hacienda">
              <PreciosStrip data={precios}/>
            </Section>

            {/* Precios de semillas (siempre visible) */}
            <Section label="Precios semillas">
              <PreciosStrip data={semillas}/>
            </Section>

            <CollapsibleSection label="Potreros" defaultOpen={false} badge={potreros.length||null}>
              {potreros.length===0
                ? <div style={{ textAlign:"center", padding:"8px 0 14px" }}>
                    <div style={{ fontSize:12, color:C.muted }}>
                      Dibujá tu primer potrero en el mapa →
                    </div>
                  </div>
                : potreros.map(p=>(
                  <div key={p.id} onClick={()=>{ setSelected(p.id); setShowFicha(true); }}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                      padding:"9px 10px", borderRadius:9, marginBottom:5, cursor:"pointer",
                      background:selected===p.id?C.pastoLight+"22":C.arena,
                      border:selected===p.id?`1.5px solid ${C.pasto}`:"1.5px solid transparent" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ width:9,height:9,borderRadius:2,background:p.color,flexShrink:0 }}/>
                      <div>
                        <div style={{ fontSize:13,fontWeight:500,color:C.texto }}>{p.nombre}</div>
                        <div style={{ fontSize:11,color:C.muted }}>{p.pasto} · {p.ha.toFixed(1)} ha · {(p.animalesAsignados||[]).length} anim.</div>
                      </div>
                    </div>
                    <Badge color={p.estado}>{ESTADO_LABEL[p.estado]}</Badge>
                  </div>
                ))
              }

              {/* Controles de dibujo */}
              {!drawing ? (
                <Btn variant="primary" onClick={()=>{ setDrawing(true); setCurrentPts([]); }}
                  style={{ width:"100%", marginTop:6 }}>
                  + Dibujar potrero
                </Btn>
              ) : (
                <div style={{ marginTop:6 }}>
                  <div style={{ fontSize:11, color:C.muted, textAlign:"center", marginBottom:6 }}>
                    {currentPts.length} vértice{currentPts.length!==1?"s":""} marcados
                    {currentPts.length<3&&" · mínimo 3"}
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    <Btn size="sm" variant="ghost" onClick={()=>{ setDrawing(false); setCurrentPts([]); }} style={{flex:1}}>
                      Cancelar
                    </Btn>
                    <Btn size="sm" variant="primary" onClick={handleClosePotrero} disabled={currentPts.length<3} style={{flex:1}}>
                      ✓ Cerrar
                    </Btn>
                  </div>
                </div>
              )}
            </CollapsibleSection>

            {/* Tareas de la agenda (separadas de las alertas) — clickeables */}
            <CollapsibleSection label="Tareas pendientes" defaultOpen={tPend.length>0}
              badge={tPend.length || null}>
              {tPend.length===0 ? (
                <div style={{ fontSize:12, color:C.muted, lineHeight:1.5 }}>
                  Sin tareas pendientes.
                  <button onClick={()=>setActiveNav("agenda")} style={{ background:"none", border:"none",
                    color:C.pasto, cursor:"pointer", fontSize:12, padding:0, marginLeft:4, fontWeight:600 }}>
                    Ir a la Agenda →
                  </button>
                </div>
              ) : (
                <>
                  {[...tVenc.map(t=>({t,g:"v"})), ...tSem.map(t=>({t,g:"s"})), ...tProx.map(t=>({t,g:"p"}))].map(({t,g})=>{
                    const pot = potreros.find(p=>p.id===t.potreroId);
                    const zona = zonas.find(z=>z.id===t.zonaId);
                    const lugar = pot?`🗺 ${pot.nombre}`:zona?`${TIPOS_ZONA[zona.tipo]?.icon||"🏠"} ${zona.nombre}`:"todo el campo";
                    const d = duTarea(t.fecha);
                    const stl = g==="v" ? {bg:"#FAE8E8",bd:"#E09090",cl:C.danger}
                              : g==="s" ? {bg:"#FFF8E8",bd:"#F0C060",cl:"#9A7B1E"}
                              :           {bg:C.arena, bd:C.arenaDark, cl:C.muted};
                    return (
                      <div key={t.id} onClick={()=>setActiveNav("agenda")} title="Ir a la Agenda"
                        style={{ display:"flex", alignItems:"center", gap:9, cursor:"pointer",
                          background: stl.bg, border:`1px solid ${stl.bd}`,
                          borderRadius:8, padding:"8px 10px", marginBottom:6 }}>
                        <span style={{ fontSize:15 }}>📅</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, fontWeight:600, color:C.tierra }}>{t.titulo}</div>
                          <div style={{ fontSize:10.5, color:C.muted }}>
                            {t.tipo} · {lugar}
                          </div>
                        </div>
                        <div style={{ fontSize:11, fontWeight:600, color: stl.cl, whiteSpace:"nowrap" }}>
                          {d<0 ? `hace ${-d}d` : d===0?"hoy":`en ${d}d`}
                        </div>
                      </div>
                    );
                  })}
                  <button onClick={()=>setActiveNav("agenda")} style={{ background:"none", border:"none",
                    color:C.pasto, cursor:"pointer", fontSize:12, padding:"2px 0", fontWeight:600 }}>
                    Ver toda la agenda →
                  </button>
                </>
              )}
            </CollapsibleSection>

            {/* Stock bajo (insumos + sanidad) */}
            <CollapsibleSection label="Stock bajo" defaultOpen={stockAlertas.length>0} badge={stockAlertas.length || null}>
              {stockAlertas.length===0 ? (
                <div style={{ fontSize:12, color:C.muted }}>Todo el stock por encima del mínimo. ✓</div>
              ) : stockAlertas.map(a=>(
                <div key={a.id} onClick={()=>setActiveNav(a.nav)} title="Ir a la sección"
                  style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, cursor:"pointer",
                    background:"#FFF8E8", border:"1px solid #F0C060", borderRadius:8, padding:"8px 10px", marginBottom:6 }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:12.5, fontWeight:600, color:C.tierra }}>{a.nombre}</div>
                    <div style={{ fontSize:10.5, color:"#7A5800" }}>{a.det}</div>
                  </div>
                  <span style={{ fontSize:15 }}>⚠</span>
                </div>
              ))}
            </CollapsibleSection>

            {/* Alertas: dinámicas (clima real de tus potreros) + contexto estacional */}
            <CollapsibleSection label="Alertas climáticas · tus potreros" defaultOpen={false} badge={alertasClima.length + ALERTAS_AGRO.length}>
              <div style={{ fontSize:10.5, color:C.muted, marginBottom:10, lineHeight:1.5 }}>
                {potreros.length>0
                  ? "🛰️ Pronóstico real en la ubicación de tus potreros, más contexto estacional de Uruguay."
                  : "Definí potreros para recibir alertas de clima de tu ubicación. Abajo, contexto estacional general."}
              </div>
              {[...alertasClima, ...ALERTAS_AGRO].map((a, i) => {
                const s = {
                  ok:     { bg:"#D4F0D8", border:"#80C890", tx:"#1A5228" },
                  warn:   { bg:"#FFF8E8", border:"#F0C060", tx:"#7A5800" },
                  danger: { bg:"#FAE8E8", border:"#E09090", tx:"#8A1C1C" },
                }[a.nivel];
                return (
                  <div key={i} style={{ background:s.bg, border:`1px solid ${s.border}`,
                    borderRadius:8, padding:"9px 11px", marginBottom:7 }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:6, marginBottom:4 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <span style={{ fontSize:14 }}>{a.ic}</span>
                        <span style={{ fontSize:12, fontWeight:600, color:s.tx }}>{a.title}</span>
                      </div>
                      {a.periodo && (
                        <span style={{ fontSize:9.5, fontFamily:"monospace", color:s.tx, opacity:0.7,
                          background:"rgba(255,255,255,0.5)", borderRadius:4, padding:"2px 5px", whiteSpace:"nowrap" }}>
                          📅 {a.periodo}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:11, color:s.tx, opacity:0.88, lineHeight:1.5 }}>{a.desc}</div>
                    {a.fuente && (
                      <div style={{ fontSize:9.5, color:s.tx, opacity:0.6, marginTop:4, fontFamily:"monospace" }}>
                        Fuente: {a.fuente}
                      </div>
                    )}
                  </div>
                );
              })}
            </CollapsibleSection>
          </aside>

          {/* Handle para redimensionar el sidebar (solo desktop) */}
          {!isMobile && (
            <div onMouseDown={startResize} title="Arrastrá para ajustar el ancho"
              style={{ width:7, flexShrink:0, cursor:"col-resize", background:C.arenaDark,
                position:"relative", zIndex:600 }}
              onMouseEnter={e=>e.currentTarget.style.background=C.pasto}
              onMouseLeave={e=>e.currentTarget.style.background=C.arenaDark}/>
          )}

          {/* Mapa Leaflet — hint bar y leyenda viven dentro del componente */}
          <MapaLeaflet
            potreros={potreros}
            drawing={drawing}
            currentPts={currentPts}
            onPoint={handleMapPoint}
            onFinish={handleMapFinish}
            onSelectPotrero={(id)=>{ setSelected(id); setShowFicha(true); }}
            selectedId={selected}
            editingId={editandoForma}
            onVertexDrag={moverVertice}
            onVertexDelete={eliminarVertice}
            onVertexInsert={insertarVertice}
            onRemovePoint={removeDrawPoint}
            onFinishEdit={async ()=>{
              // Persistir la nueva forma (vértices/ha) en BD al confirmar la edición
              const pot = potreros.find(p=>p.id===editandoForma);
              if (pot) { try { await updatePotrero(pot); } catch (e) { console.error("Error guardando forma del potrero:", e); } }
              setEditandoForma(null); setFormaOriginal(null);
            }}
            onCancelEdit={()=>{
              if (formaOriginal) setPotreros(prev=>prev.map(p=>
                p.id===formaOriginal.id ? { ...p, points:formaOriginal.points, ha:formaOriginal.ha } : p));
              setEditandoForma(null); setFormaOriginal(null);
            }}
          />
        </div>
      )}

      {activeNav==="rodeo" && (
        <RodeoPanel potreros={potreros} vendidos={vendidos} user={user} profile={profile}
          onUpdateAnimal={updateAnimal} onMoverAnimal={moverAnimal}
          onEliminarAnimal={eliminarAnimal} onAgregarAnimal={agregarAnimal}
          onVender={venderAnimal} onRevertirVenta={revertirVenta}/>
      )}
      {activeNav==="sanidad" && (
        <SanidadPanel potreros={potreros} stockSanidad={stockSanidad}
          onAplicarTratamiento={aplicarTratamiento}
          onCrearStock={sanidadState.createStock} onEliminarStock={sanidadState.deleteStock}/>
      )}
      {activeNav==="reproduccion" && (
        <ReproduccionPanel eventos={eventosCampo} pesadas={pesadasCampo}
          animales={todosLosAnimales.filter(a=>(a.estado||"activo")==="activo")}
          onAddEvento={addEventoRepro}/>
      )}
      {activeNav==="economia" && (
        <EconomiaPanel transacciones={transacciones}
          onAddTransaccion={addTransaccion} totalHa={totalHa} totalAnim={totalAnim}/>
      )}
      {activeNav==="fardos" && (
        <FardosPanel fardosState={fardosState} potreros={potreros} user={user} profile={profile}/>
      )}
      {activeNav==="insumos" && (
        <InsumosPanel insumosState={insumosState} user={user} profile={profile} onGasto={addTransaccion}/>
      )}
      {activeNav==="agenda" && (
        <CalendarioPanel tareas={tareas} potreros={potreros} zonas={zonas} onUpdate={setTareas}
          maquinas={maquinarias} empleados={empleados} user={user} profile={profile}
          tareasState={tareasState} empleadosState={empleadosState} maquinariasState={maquinariasState}/>
      )}
      {activeNav==="monitor" && (
        <MonitorPanel tareas={tareas} empleados={empleados} maquinas={maquinarias}/>
      )}
      {activeNav==="gestion" && (
        <GestionPanel maquinariasState={maquinariasState} empleadosState={empleadosState} user={user} profile={profile}/>
      )}
      {activeNav==="zonas" && (
        <ZonasPanel zonasState={zonasState} insumosState={insumosState} user={user} profile={profile}
          onIngreso={addTransaccion} onConsumirRacion={consumirRacion}/>
      )}
      {activeNav==="publicaciones" && (
        <PublicacionesPanel user={user} profile={profile} onIngreso={addTransaccion}/>
      )}
      {activeNav==="marketplace" && <MarketplacePanel/>}
      {activeNav==="admin" && isSuperAdmin && <AdminPanel/>}
        </div>{/* /contenido */}
        {isMobile && bottomNav}
      </div>{/* /columna principal */}

      {moreSheet}

      <Buscador open={showBuscar} onClose={()=>setShowBuscar(false)} onNav={setActiveNav}
        potreros={potreros} insumos={insumos} zonas={zonas} tareas={tareas}
        maquinarias={maquinarias} empleados={empleados} stockSanidad={stockSanidad}/>

      {/* ── MODALES ── */}
      {pendingPts && (
        <ModalNuevoPotrero count={potreros.length+1}
          onSave={handleSaveNewPot} onCancel={()=>setPendingPts(null)}/>
      )}
      {showFicha && potreroSel && (
        <FichaPotrero pot={potreroSel} potreros={potreros}
          onClose={()=>setShowFicha(false)} onUpdate={updatePotrero}
          onDelete={()=>deletePotrero(potreroSel.id)}
          onEditShape={()=>{ setShowFicha(false); setSelected(null); setFormaOriginal({ id:potreroSel.id, points:potreroSel.points, ha:potreroSel.ha }); setEditandoForma(potreroSel.id); }}
          onConsumir={consumirInsumos} insumos={insumos} onEnfardar={enfardarDesdeActividad}
          onUpdateAnimal={updateAnimal} onMoverAnimal={moverAnimal} onVender={venderAnimal}
          user={user} profile={profile}/>
      )}
      {showIA && <PanelIA potreros={potreros} transacciones={transacciones} precios={precios} ubicacion={ubicacion} onClose={()=>setShowIA(false)}/>}
      {showMisDatos && (
        <ModalMisDatos
          user={user} profile={profile}
          onClose={()=>setShowMisDatos(false)}
          onSaved={(p)=>{
            if (p.nombre_campo) setNombreCampo(p.nombre_campo);
            if (p.ubicacion!==undefined) setUbicacion(p.ubicacion);
            if (p.features) setFeatures(p.features);
            onProfileUpdate && onProfileUpdate(p);
          }}
        />
      )}
      {mostrarCrearCampo && (
        <ModalCrearCampo
          onCrear={crearNuevoCampo}
          nombreNuevo={nombreNuevoCampo}
          setNombreNuevo={setNombreNuevoCampo}
          ubicacionNueva={ubicacionNuevoCampo}
          setUbicacionNueva={setUbicacionNuevoCampo}
          onCancel={()=>setMostrarCrearCampo(false)}
        />
      )}
    </div>
  );
}

// ─── MODAL MIS DATOS ──────────────────────────────────────
function ModalMisDatos({ user, profile, onClose, onSaved }) {
  const [phone,       setPhone]       = useState(profile?.phone || "+598");
  const [nombreCampo, setNombreCampo] = useState(profile?.nombre_campo || "");
  const [ubicacion,   setUbicacion]   = useState(profile?.ubicacion || "");
  const [loading,     setLoading]     = useState(false);
  const [msg,         setMsg]         = useState("");

  const guardar = async () => {
    setLoading(true); setMsg("");
    try {
      const upd = { phone, nombre_campo: nombreCampo, ubicacion };
      await perfilService.actualizar(upd);
      onSaved && onSaved(upd);
      setMsg("ok");
      setTimeout(onClose, 700);
    } catch (e) {
      setMsg(e.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Mis datos" onClose={onClose} width={420}>
      <div style={{ fontSize:12, color:C.muted, marginBottom:14 }}>
        Estos datos son tuyos y solo los ves vos.
      </div>

      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>Email (no editable)</div>
        <div style={{ background:C.arena, borderRadius:8, padding:"9px 12px", fontSize:13,
          color:C.texto, fontFamily:"monospace" }}>{user?.email}</div>
      </div>

      <Input label="Celular (WhatsApp)" value={phone} onChange={setPhone} type="tel"
        placeholder="+598 99 123 456"/>
      <Input label="Nombre del campo" value={nombreCampo} onChange={setNombreCampo}
        placeholder="Ej: El Paraíso"/>
      <Input label="Ubicación (Depto.)" value={ubicacion} onChange={setUbicacion}
        placeholder="Ej: Soriano"/>

      {msg==="ok" && (
        <div style={{ background:"#D4F0D8", color:"#1A5228", padding:"9px 12px", borderRadius:8,
          fontSize:12, textAlign:"center", marginBottom:12 }}>✓ Datos guardados</div>
      )}
      {msg && msg!=="ok" && (
        <div style={{ background:"#FAE8E8", color:C.danger, padding:"9px 12px", borderRadius:8,
          fontSize:12, textAlign:"center", marginBottom:12 }}>{msg}</div>
      )}

      <Btn variant="primary" onClick={guardar} disabled={loading} style={{ width:"100%" }}>
        {loading ? "Guardando..." : "Guardar cambios"}
      </Btn>
    </Modal>
  );
}

// ─── MODAL CREAR NUEVO CAMPO ──────────────────────────────────────
function ModalCrearCampo({ onCrear, nombreNuevo, setNombreNuevo, ubicacionNueva, setUbicacionNueva, onCancel }) {
  const [loading, setLoading] = useState(false);

  const handleCrear = async () => {
    if (!nombreNuevo.trim()) return;
    setLoading(true);
    try {
      await onCrear();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Crear nuevo campo" onClose={onCancel} width={420}>
      <div style={{ fontSize:12, color:C.muted, marginBottom:14 }}>
        Ingresa los datos básicos del nuevo campo. Podrás editarlos después.
      </div>

      <Input label="Nombre del campo" value={nombreNuevo} onChange={setNombreNuevo}
        placeholder="Ej: La Estancia Nueva" autoFocus/>
      <Input label="Ubicación (Depto.)" value={ubicacionNueva} onChange={setUbicacionNueva}
        placeholder="Ej: Paysandú"/>

      <div style={{ display:"flex", gap:10, marginTop:20 }}>
        <Btn variant="secondary" onClick={onCancel} disabled={loading} style={{ flex:1 }}>
          Cancelar
        </Btn>
        <Btn variant="primary" onClick={handleCrear} disabled={loading || !nombreNuevo.trim()} style={{ flex:1 }}>
          {loading ? "Creando..." : "Crear campo"}
        </Btn>
      </div>
    </Modal>
  );
}
